import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../contexts/AuthContext';
import './Account.css';
import './MyKits.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const formatPrice = (price) =>
  price != null ? `₹${parseFloat(price).toFixed(2)}` : '—';

const r2 = (n) => Math.round(n * 100) / 100;

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) { resolve(true); return; }
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload  = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
};

const statusLabel = (order) => {
  const s = order.order_lc_state || order.order_state || '—';
  return s;
};

const statusClass = (order) => {
  const s = (order.order_lc_state || order.order_state || '').toLowerCase();
  if (s === 'placed' || s === 'draft') return 'status-placed';
  if (s === 'delivered') return 'status-delivered';
  if (s === 'cancelled') return 'status-cancelled';
  return 'status-default';
};

// ── Pay Now modal ─────────────────────────────────────────
const PaymentModal = ({ order, user, onClose, onPaid }) => {
  const auth = { Authorization: `Bearer ${user.token}` };
  const [walletBalance, setWalletBalance] = useState(null);
  const [cashUsed, setCashUsed]     = useState(0);
  const [pointsUsed, setPointsUsed] = useState(0);
  const [phase, setPhase]           = useState('loading');
  const [error, setError]           = useState('');
  const [rzOrderId, setRzOrderId]   = useState(null);

  const orderAmount = parseFloat(order.order_amount);

  useEffect(() => {
    axios.get(`${API_URL}/wallet/balance`, { headers: auth })
      .then(res => {
        const bal = res.data;
        setWalletBalance(bal);
        const maxCash   = r2(Math.min(parseFloat(bal.cash_balance   || 0), orderAmount));
        const maxPoints = r2(Math.min(parseFloat(bal.points_balance || 0), Math.max(0, orderAmount - maxCash)));
        setCashUsed(maxCash);
        setPointsUsed(maxPoints);
        setPhase('ready');
      })
      .catch(() => { setWalletBalance(null); setPhase('ready'); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasCash    = parseFloat(walletBalance?.cash_balance   || 0) > 0;
  const hasPoints  = parseFloat(walletBalance?.points_balance || 0) > 0;
  const hasWallet  = hasCash || hasPoints;
  const razorpayDue = r2(Math.max(0, orderAmount - cashUsed - pointsUsed));

  const cancelPending = (orderId) => {
    if (!orderId) return;
    axios.post(`${API_URL}/pay/pay-existing/cancel`, { razorpay_order_id: orderId }, { headers: auth })
      .catch(() => { /* fire-and-forget */ });
    setRzOrderId(null);
  };

  const handlePay = async () => {
    setError('');
    setPhase('processing');
    const payload = { order_id: order.id, wallet_cash_amount: cashUsed, wallet_points_amount: pointsUsed };

    // ── Pure wallet ──────────────────────────────────────────────────
    if (razorpayDue < 0.01 && (cashUsed > 0 || pointsUsed > 0)) {
      try {
        await axios.post(`${API_URL}/pay/pay-existing/create-order`, payload, { headers: auth });
        onPaid(order.id);
      } catch (err) {
        setError(err.response?.data?.error || 'Payment failed. Please try again.');
        setPhase('ready');
      }
      return;
    }

    // ── Razorpay (full or split) ─────────────────────────────────────
    const loaded = await loadRazorpayScript();
    if (!loaded) {
      setError('Failed to load payment SDK. Please check your connection.');
      setPhase('ready');
      return;
    }

    try {
      const { data } = await axios.post(`${API_URL}/pay/pay-existing/create-order`, payload, { headers: auth });

      setRzOrderId(data.razorpay_order_id);

      const rzp = new window.Razorpay({
        key:      data.key,
        amount:   data.amount,
        currency: data.currency,
        order_id: data.razorpay_order_id,
        name:        'Local Heros',
        description: `Payment for order ${order.order_no}`,
        prefill: { name: user.name, email: user.email, contact: user.phone },
        theme: { color: '#C9A227' },
        handler: async (response) => {
          try {
            await axios.post(`${API_URL}/pay/pay-existing/verify`, {
              order_id:            order.id,
              razorpay_order_id:   response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature:  response.razorpay_signature,
              wallet_cash_amount:   cashUsed,
              wallet_points_amount: pointsUsed,
            }, { headers: auth });
            onPaid(order.id);
          } catch (err) {
            setError(err.response?.data?.error || 'Payment received but order update failed. Please contact support.');
            setPhase('ready');
          }
        },
        modal: {
          ondismiss: () => {
            cancelPending(data.razorpay_order_id);
            setPhase('ready');
          },
        },
      });
      rzp.on('payment.failed', (response) => {
        cancelPending(data.razorpay_order_id);
        setError(`Payment failed: ${response.error.description}`);
        setPhase('ready');
      });
      rzp.open();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to initiate payment. Please try again.');
      setPhase('ready');
    }
  };

  const btnLabel = razorpayDue < 0.01
    ? 'Pay with Wallet'
    : (cashUsed > 0 || pointsUsed > 0)
      ? `Pay ₹${razorpayDue.toFixed(2)} via Razorpay`
      : `Pay ₹${orderAmount.toFixed(2)}`;

  return (
    <div className="acct-pay-overlay" onClick={() => phase !== 'processing' && onClose()}>
      <div className="acct-pay-modal" onClick={e => e.stopPropagation()}>
        <div className="acct-pay-header">
          <h3>Pay for Order {order.order_no}</h3>
          <button className="acct-pay-close" onClick={onClose} disabled={phase === 'processing'}>×</button>
        </div>

        <div className="acct-pay-body">
          <div className="acct-pay-amount-row">
            <span>Order Amount</span>
            <strong>{formatPrice(order.order_amount)}</strong>
          </div>

          {phase === 'loading' && <p className="acct-loading" style={{ margin: '12px 0 0' }}>Loading wallet…</p>}

          {phase !== 'loading' && hasWallet && (
            <div className="acct-pay-wallet-section">
              <p className="acct-pay-wallet-title">Use Wallet Balance</p>
              {hasCash && (
                <div className="acct-pay-wallet-row">
                  <span>Cash <span className="acct-pay-avail">(Available: ₹{parseFloat(walletBalance.cash_balance).toFixed(2)})</span></span>
                  <input
                    type="number"
                    className="acct-pay-wallet-input"
                    min="0"
                    max={Math.min(parseFloat(walletBalance.cash_balance), orderAmount)}
                    step="0.01"
                    value={cashUsed}
                    onChange={e => {
                      const v = parseFloat(e.target.value) || 0;
                      const maxCash = Math.min(parseFloat(walletBalance.cash_balance), orderAmount);
                      const clamped = r2(Math.max(0, Math.min(v, maxCash)));
                      setCashUsed(clamped);
                      const maxPts = Math.min(parseFloat(walletBalance.points_balance), Math.max(0, orderAmount - clamped));
                      setPointsUsed(r2(Math.min(pointsUsed, maxPts)));
                    }}
                  />
                </div>
              )}
              {hasPoints && (
                <div className="acct-pay-wallet-row">
                  <span>Points <span className="acct-pay-avail">(Available: {parseFloat(walletBalance.points_balance).toFixed(0)} pts)</span></span>
                  <input
                    type="number"
                    className="acct-pay-wallet-input"
                    min="0"
                    max={Math.min(parseFloat(walletBalance.points_balance), Math.max(0, orderAmount - cashUsed))}
                    step="0.01"
                    value={pointsUsed}
                    onChange={e => {
                      const v = parseFloat(e.target.value) || 0;
                      const remaining = Math.max(0, orderAmount - cashUsed);
                      const maxPts = Math.min(parseFloat(walletBalance.points_balance), remaining);
                      setPointsUsed(r2(Math.max(0, Math.min(v, maxPts))));
                    }}
                  />
                </div>
              )}
              {razorpayDue > 0.01 && (
                <div className="acct-pay-due-row">
                  <span>To pay via Razorpay</span>
                  <strong>₹{razorpayDue.toFixed(2)}</strong>
                </div>
              )}
            </div>
          )}

          {error && <p className="acct-pay-error">{error}</p>}
        </div>

        <div className="acct-pay-footer">
          <button className="acct-pay-cancel-btn" onClick={onClose} disabled={phase === 'processing'}>
            Close
          </button>
          <button
            className="acct-pay-btn"
            onClick={handlePay}
            disabled={phase === 'loading' || phase === 'processing'}
          >
            {phase === 'processing' ? 'Processing…' : btnLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Kit helpers ───────────────────────────────────────────
const DOW_MYK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const STATE_ICON_MYK = {
  Delivered: '✅', Dispatched: '🚚', Scheduled: '📦', Rescheduled: '🔄', Cancelled: '❌',
};

const fmtDateMyk = (s) => {
  if (!s) return '—';
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-IN', {
    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
  });
};

const isActionable = (delivery) => {
  if (delivery.order_state !== 'Scheduled') return false;
  const now = new Date();
  const cutoff = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (now.getHours() >= 22) cutoff.setDate(cutoff.getDate() + 1);
  const [y, m, d] = delivery.delivery_date.split('-').map(Number);
  return new Date(y, m - 1, d) > cutoff;
};

const isActionablePubPmb = (child) => {
  if (!['Placed', 'Scheduled'].includes(child.order_lc_state)) return false;
  const now = new Date();
  const cutoff = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (now.getHours() >= 22) cutoff.setDate(cutoff.getDate() + 1);
  const [y, m, d] = child.delivery_date.split('-').map(Number);
  return new Date(y, m - 1, d) > cutoff;
};

function itemsPerDelivery(deliveryDays, consumptionDays) {
  if (!deliveryDays?.length || !consumptionDays?.length) return {};
  const sorted = [...deliveryDays].sort((a, b) => a - b);
  const result = {};
  for (let i = 0; i < sorted.length; i++) {
    const d    = sorted[i];
    const next = sorted[(i + 1) % sorted.length];
    let count  = 0;
    if (next > d) {
      for (const c of consumptionDays) { if (c > d && c <= next) count++; }
    } else {
      for (const c of consumptionDays) { if (c > d || c <= next) count++; }
    }
    result[d] = count;
  }
  return result;
}

function dowOf(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d).getDay();
}

// ── Kit payment modal ─────────────────────────────────────
const KitPaymentModal = ({ order, user, onClose, onPaid }) => {
  const auth = { Authorization: `Bearer ${user?.token}` };
  const [walletBalance, setWalletBalance] = useState(null);
  const [cashUsed, setCashUsed]     = useState(0);
  const [pointsUsed, setPointsUsed] = useState(0);
  const [phase, setPhase]           = useState('loading');
  const [error, setError]           = useState('');
  const [rzOrderId, setRzOrderId]   = useState(null); // eslint-disable-line no-unused-vars

  const orderAmount = parseFloat(order.parent_order_amount);

  useEffect(() => {
    axios.get(`${API_URL}/wallet/balance`, { headers: auth })
      .then(res => {
        const bal = res.data;
        setWalletBalance(bal);
        const maxCash   = r2(Math.min(parseFloat(bal.cash_balance   || 0), orderAmount));
        const maxPoints = r2(Math.min(parseFloat(bal.points_balance || 0), Math.max(0, orderAmount - maxCash)));
        setCashUsed(maxCash);
        setPointsUsed(maxPoints);
        setPhase('ready');
      })
      .catch(() => { setWalletBalance(null); setPhase('ready'); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasCash   = parseFloat(walletBalance?.cash_balance   || 0) > 0;
  const hasPoints = parseFloat(walletBalance?.points_balance || 0) > 0;
  const hasWallet = hasCash || hasPoints;
  const razorpayDue = r2(Math.max(0, orderAmount - cashUsed - pointsUsed));

  const cancelPending = (rzId) => {
    if (!rzId) return;
    axios.post(`${API_URL}/pay/pay-existing/cancel`, { razorpay_order_id: rzId }, { headers: auth }).catch(() => {});
    setRzOrderId(null);
  };

  const handlePay = async () => {
    setError('');
    setPhase('processing');
    const payload = { order_id: order.parent_order_id, wallet_cash_amount: cashUsed, wallet_points_amount: pointsUsed };

    if (razorpayDue < 0.01 && (cashUsed > 0 || pointsUsed > 0)) {
      try {
        await axios.post(`${API_URL}/pay/pay-existing/create-order`, payload, { headers: auth });
        onPaid();
      } catch (err) {
        setError(err.response?.data?.error || 'Payment failed. Please try again.');
        setPhase('ready');
      }
      return;
    }

    const loaded = await loadRazorpayScript();
    if (!loaded) { setError('Failed to load payment SDK.'); setPhase('ready'); return; }

    try {
      const { data } = await axios.post(`${API_URL}/pay/pay-existing/create-order`, payload, { headers: auth });
      setRzOrderId(data.razorpay_order_id);
      const rzp = new window.Razorpay({
        key: data.key, amount: data.amount, currency: data.currency, order_id: data.razorpay_order_id,
        name: 'Local Heros', description: `Payment for order ${order.parent_order_no}`,
        prefill: { name: user?.name, email: user?.email, contact: user?.phone },
        theme: { color: '#C9A227' },
        handler: async (response) => {
          try {
            await axios.post(`${API_URL}/pay/pay-existing/verify`, {
              order_id: order.parent_order_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              wallet_cash_amount: cashUsed,
              wallet_points_amount: pointsUsed,
            }, { headers: auth });
            onPaid();
          } catch (err) {
            setError(err.response?.data?.error || 'Payment received but order update failed.');
            setPhase('ready');
          }
        },
        modal: { ondismiss: () => { cancelPending(data.razorpay_order_id); setPhase('ready'); } },
      });
      rzp.on('payment.failed', (response) => {
        cancelPending(data.razorpay_order_id);
        setError(`Payment failed: ${response.error.description}`);
        setPhase('ready');
      });
      rzp.open();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to initiate payment.');
      setPhase('ready');
    }
  };

  const btnLabel = razorpayDue < 0.01
    ? 'Pay with Wallet'
    : (cashUsed > 0 || pointsUsed > 0)
      ? `Pay ₹${razorpayDue.toFixed(2)} via Razorpay`
      : `Pay ₹${orderAmount.toFixed(2)}`;

  return (
    <div className="myk-modal-overlay" onClick={() => phase !== 'processing' && onClose()}>
      <div className="myk-modal" onClick={e => e.stopPropagation()}>
        <div className="myk-modal-header">
          <h3>Pay for Order {order.parent_order_no}</h3>
          <button className="myk-modal-close" onClick={onClose} disabled={phase === 'processing'}>×</button>
        </div>
        <div className="myk-modal-body">
          {phase === 'loading' && <p className="myk-empty">Loading…</p>}
          {error && <p className="myk-error">{error}</p>}
          {phase !== 'loading' && (
            <>
              <div className="myk-pay-amount-row">
                <span>Order Total</span>
                <strong>₹{orderAmount.toFixed(2)}</strong>
              </div>
              {hasWallet && (
                <div className="myk-pay-wallet-section">
                  {hasCash && (
                    <label className="myk-pay-wallet-row">
                      <span>Wallet Cash (₹{parseFloat(walletBalance.cash_balance).toFixed(2)} available)</span>
                      <input type="number" min={0} max={parseFloat(walletBalance.cash_balance)}
                        step="0.01" value={cashUsed}
                        onChange={e => setCashUsed(r2(Math.min(parseFloat(e.target.value) || 0, parseFloat(walletBalance.cash_balance), orderAmount)))}
                        className="myk-pay-wallet-input" disabled={phase === 'processing'} />
                    </label>
                  )}
                  {hasPoints && (
                    <label className="myk-pay-wallet-row">
                      <span>Wallet Points (₹{parseFloat(walletBalance.points_balance).toFixed(2)} available)</span>
                      <input type="number" min={0} max={parseFloat(walletBalance.points_balance)}
                        step="0.01" value={pointsUsed}
                        onChange={e => setPointsUsed(r2(Math.min(parseFloat(e.target.value) || 0, parseFloat(walletBalance.points_balance), Math.max(0, orderAmount - cashUsed))))}
                        className="myk-pay-wallet-input" disabled={phase === 'processing'} />
                    </label>
                  )}
                  {razorpayDue > 0.01 && (
                    <div className="myk-pay-amount-row">
                      <span>To pay via Razorpay</span>
                      <strong>₹{razorpayDue.toFixed(2)}</strong>
                    </div>
                  )}
                </div>
              )}
              <div className="myk-modal-footer">
                <button className="myk-btn myk-btn--back" onClick={onClose} disabled={phase === 'processing'}>Cancel</button>
                <button className="myk-btn myk-btn--pay" onClick={handlePay}
                  disabled={phase === 'processing' || (razorpayDue < 0.01 && cashUsed < 0.01 && pointsUsed < 0.01)}>
                  {phase === 'processing' ? 'Processing…' : btnLabel}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ── PDF Renderer (canvas-based, works on iOS) ──────────────
const PdfPages = ({ url, token }) => {
  const containerRef = useRef(null);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    if (!url) return;
    let cancelled = false;
    setStatus('loading');

    (async () => {
      try {
        const pdfjs = await import('pdfjs-dist/webpack');
        const bytes = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
        }).then(r => r.arrayBuffer());
        if (cancelled) return;

        const pdf = await pdfjs.getDocument({ data: bytes }).promise;
        if (cancelled) return;

        const container = containerRef.current;
        if (!container) return;
        container.innerHTML = '';

        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
          if (cancelled) return;
          const page = await pdf.getPage(pageNum);
          const baseVp = page.getViewport({ scale: 1 });
          const dpr = window.devicePixelRatio || 1;
          const scale = (Math.min(window.innerWidth, 800) / baseVp.width) * dpr;
          const viewport = page.getViewport({ scale });
          const canvas = document.createElement('canvas');
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          canvas.style.cssText = 'width:100%;display:block;margin-bottom:4px;background:#fff;';
          container.appendChild(canvas);
          await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
          if (cancelled) return;
        }
        if (!cancelled) setStatus('done');
      } catch {
        if (!cancelled) setStatus('error');
      }
    })();

    return () => { cancelled = true; };
  }, [url, token]);

  return (
    <div className="acct-pdf-wrap">
      {status === 'loading' && <div className="acct-pdf-status">Loading document…</div>}
      {status === 'error' && (
        <div className="acct-pdf-status acct-pdf-status--error">
          Could not display document. Use Download to view it.
        </div>
      )}
      <div ref={containerRef} />
    </div>
  );
};

// ── Health Documents Modal ─────────────────────────────────
const DOC_TYPES = ['Lab Report', 'Goal Sheet', 'Diet / Nutrition Plan', 'Blood Work', 'Fitness Assessment', 'Progress Report', 'Medical History', 'Other'];

const HealthDocsModal = ({ order, user, onClose }) => {
  const authHdr = { headers: { Authorization: `Bearer ${user.token}` } };
  const [docs, setDocs]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewUrl, setViewUrl]         = useState(null);
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/health-documents`, { params: { order_id: order.id }, ...authHdr });
      setDocs(r.data || []);
    } catch { setDocs([]); } finally { setLoading(false); }
  }, [order.id]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const res = await fetch(viewUrl, { headers: { Authorization: `Bearer ${user.token}` } });
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = viewUrl.split('/').pop() || 'document.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objUrl);
    } catch {
      window.open(viewUrl, '_blank');
    } finally {
      setDownloading(false);
    }
  };

  if (viewUrl) {
    return (
      <div className="acct-doc-viewer">
        <div className="acct-doc-viewer-bar">
          <button className="acct-doc-viewer-back" onClick={() => setViewUrl(null)}>&#8592; Back</button>
          <button className="acct-doc-viewer-download" onClick={handleDownload} disabled={downloading}>
            {downloading ? 'Downloading…' : '⬇ Download'}
          </button>
        </div>
        <div className="acct-doc-viewer-scroll">
          <PdfPages url={viewUrl} token={user.token} />
        </div>
      </div>
    );
  }

  return (
    <div className="acct-modal-overlay" onClick={onClose}>
      <div className="acct-modal acct-modal--docs" onClick={e => e.stopPropagation()}>
        <div className="acct-modal-header">
          <h3>Reports / Documents — {order.order_no}</h3>
          <button className="acct-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="acct-modal-body">
          {loading ? (
            <div className="acct-docs-loading">Loading…</div>
          ) : docs.length === 0 ? (
            <div className="acct-docs-empty">No documents uploaded yet.</div>
          ) : (
            <table className="acct-docs-table">
              <thead><tr><th>Type</th><th>Name</th><th>Date</th><th></th></tr></thead>
              <tbody>
                {docs.map(d => (
                  <tr key={d.id}>
                    <td>{d.document_type}</td>
                    <td>{d.document_name}</td>
                    <td>{new Date(d.created_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                    <td>
                      <button className="acct-docs-view-link" onClick={() => setViewUrl(`${API_URL}${d.document_url}`)}>View</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="acct-modal-footer">
          <button className="acct-modal-close-btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

// ── Orders tab (regular + kit combined) ───────────────────
const PAGE_SIZE = 5;

const OrdersTab = ({ user }) => {
  const authHdr = () => ({ Authorization: `Bearer ${user.token}` });

  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [page, setPage]         = useState(0);

  // Regular order expand (single open at a time)
  const [expanded, setExpanded]     = useState(null);
  const [payingOrder, setPayingOrder] = useState(null);

  // Kit expand (multiple open at a time)
  const [kitExpanded, setKitExpanded]   = useState(new Set());
  const [kitPayingOrder, setKitPayingOrder] = useState(null);

  // PMB/PUB expand
  const [pubPmbExpanded, setPubPmbExpanded] = useState(new Set());

  // Health documents
  const [docsOrder, setDocsOrder] = useState(null);

  // Product search / LOV
  const [productSearch, setProductSearch]   = useState('');
  const [activeSearch, setActiveSearch]     = useState('');
  const [prodSuggestions, setProdSuggestions] = useState([]);
  const [showProdDrop, setShowProdDrop]     = useState(false);

  // Reschedule
  const [rescheduleModal, setRescheduleModal]     = useState(null);
  const [validDates, setValidDates]               = useState([]);
  const [rescheduleLoading, setRescheduleLoading] = useState(false);
  const [rescheduleError, setRescheduleError]     = useState('');
  const [pickedDate, setPickedDate]               = useState('');

  // Cancel
  const [cancelModal, setCancelModal]           = useState(null);
  const [cancelConfirmText, setCancelConfirmText] = useState('');
  const [cancelLoading, setCancelLoading]       = useState(false);
  const [cancelError, setCancelError]           = useState('');

  const load = useCallback(async (searchTerm = '') => {
    try {
      setLoading(true);
      setError(null);
      const headers = authHdr();
      const params  = searchTerm ? { product_search: searchTerm } : {};
      const q       = searchTerm.toLowerCase();
      const [regRes, kitsRes, pubPmbRes] = await Promise.all([
        axios.get(`${API_URL}/menuRoutes/my-orders`,        { headers, params }),
        axios.get(`${API_URL}/kit-orders/my-kits`,          { headers }),
        axios.get(`${API_URL}/plan-week/pub-pmb/orders`,    { headers }),
      ]);
      const filterKit    = p => !searchTerm || (p.kits || []).some(k => k.kit_name?.toLowerCase().includes(q));
      const filterPubPmb = p => !searchTerm || (p.children || []).some(c =>
        (c.items || []).some(it => it.product_name?.toLowerCase().includes(q))
      );
      const merged = [
        ...regRes.data.map(o  => ({ type: 'regular', sortDate: o.order_date,         data: o })),
        ...kitsRes.data.filter(filterKit).map(p  => ({ type: 'kit',     sortDate: p.parent_order_date,  data: p })),
        ...pubPmbRes.data.filter(filterPubPmb).map(p => ({ type: 'pubpmb', sortDate: p.parent_order_date,  data: p })),
      ].sort((a, b) => new Date(b.sortDate) - new Date(a.sortDate));
      setAllItems(merged);
    } catch {
      setError('Could not load orders.');
    } finally {
      setLoading(false);
    }
  }, [user.token]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    axios.get(`${API_URL}/menuRoutes/my-products-lov`, { headers: authHdr() })
      .then(r => setProdSuggestions(r.data || []))
      .catch(() => {});
  }, []); // eslint-disable-line

  const toggleKit = (parentId) => {
    setKitExpanded(prev => {
      const next = new Set(prev);
      next.has(parentId) ? next.delete(parentId) : next.add(parentId);
      return next;
    });
  };

  const togglePubPmb = (parentId) => {
    setPubPmbExpanded(prev => {
      const next = new Set(prev);
      next.has(parentId) ? next.delete(parentId) : next.add(parentId);
      return next;
    });
  };

  const openReschedule = async (delivery, scheduleDays, modalType = 'kit') => {
    setRescheduleError('');
    setPickedDate('');
    setRescheduleModal({ delivery, scheduleDays, modalType });
    if (modalType === 'pubpmb') return; // date range computed from min/max on the picker
    try {
      const res = await axios.get(`${API_URL}/kit-orders/child/${delivery.id}/valid-dates`, { headers: authHdr() });
      setValidDates(res.data.dates || []);
    } catch {
      setRescheduleError('Could not load available dates.');
      setValidDates([]);
    }
  };

  const confirmReschedule = async (newDate) => {
    if (!newDate || !rescheduleModal) return;
    setRescheduleLoading(true);
    setRescheduleError('');
    try {
      const url = rescheduleModal.modalType === 'pubpmb'
        ? `${API_URL}/plan-week/pub-pmb/child/${rescheduleModal.delivery.id}/reschedule`
        : `${API_URL}/kit-orders/child/${rescheduleModal.delivery.id}/reschedule`;
      await axios.patch(url, { new_date: newDate }, { headers: authHdr() });
      setRescheduleModal(null);
      setValidDates([]);
      await load();
    } catch (err) {
      setRescheduleError(err.response?.data?.error || 'Failed to reschedule. Please try again.');
    } finally {
      setRescheduleLoading(false);
    }
  };

  const openCancel = (delivery, items, modalType = 'kit') => {
    setCancelError('');
    setCancelConfirmText('');
    setCancelModal({ delivery, kitItems: items, modalType });
  };

  const confirmCancel = async () => {
    if (cancelConfirmText !== 'CANCEL') { setCancelError('Please type CANCEL to confirm.'); return; }
    setCancelLoading(true);
    setCancelError('');
    try {
      const url = cancelModal.modalType === 'pubpmb'
        ? `${API_URL}/plan-week/pub-pmb/child/${cancelModal.delivery.id}/cancel`
        : `${API_URL}/kit-orders/child/${cancelModal.delivery.id}/cancel`;
      await axios.patch(url, {}, { headers: authHdr() });
      setCancelModal(null);
      await load();
    } catch (err) {
      setCancelError(err.response?.data?.error || 'Failed to cancel. Please try again.');
    } finally {
      setCancelLoading(false);
    }
  };

  if (loading) return <p className="acct-loading">Loading orders…</p>;
  if (error)   return <p className="acct-error">{error}</p>;
  if (allItems.length === 0 && !activeSearch) return (
    <div className="acct-empty"><p>You haven't placed any orders yet.</p></div>
  );

  const filteredSuggestions = productSearch
    ? prodSuggestions.filter(n => n.toLowerCase().includes(productSearch.toLowerCase()))
    : [];

  const totalPages = Math.ceil(allItems.length / PAGE_SIZE);
  const pageItems  = allItems.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const commitSearch = (term) => {
    setProductSearch(term);
    setActiveSearch(term);
    setShowProdDrop(false);
    setPage(0);
    load(term);
  };

  return (
    <div className="acct-orders">
      <div className="acct-search-row">
        <div style={{ position: 'relative', flex: 1 }}>
          <input
            type="text"
            className="acct-search-input"
            placeholder="Search by product name…"
            value={productSearch}
            onChange={e => { setProductSearch(e.target.value); setShowProdDrop(true); }}
            onBlur={() => setTimeout(() => setShowProdDrop(false), 200)}
            onKeyDown={e => { if (e.key === 'Enter' && productSearch) commitSearch(productSearch); }}
            autoComplete="off"
          />
          {showProdDrop && filteredSuggestions.length > 0 && (
            <div className="acct-prod-dropdown">
              {filteredSuggestions.map((name, i) => (
                <div key={i} className="acct-prod-item" onMouseDown={() => commitSearch(name)}>
                  {name}
                </div>
              ))}
            </div>
          )}
        </div>
        {activeSearch && (
          <button className="acct-search-clear" onClick={() => { setProductSearch(''); setActiveSearch(''); setPage(0); load(''); }}>&#x2715;</button>
        )}
      </div>

      {allItems.length === 0 && activeSearch && (
        <div className="acct-empty"><p>No orders found for "{activeSearch}".</p></div>
      )}

      {pageItems.map(item => {
        if (item.type === 'regular') {
          const order = item.data;
          return (
            <div key={`reg-${order.id}`} className="acct-order-card">
              <button
                className="acct-order-header"
                onClick={() => setExpanded(expanded === order.id ? null : order.id)}
              >
                <div className="acct-order-meta">
                  <span className="acct-order-no">{order.order_no}</span>
                  <span className="acct-order-date">{formatDate(order.order_date)}</span>
                </div>
                <div className="acct-order-right">
                  {order.order_lc_state === 'Payment Requested' ? (
                    <span className="acct-pay-now-btn"
                      onClick={e => { e.stopPropagation(); setPayingOrder(order); }}
                      role="button" tabIndex={0}>
                      Pay Now
                    </span>
                  ) : (
                    <span className={`acct-order-status ${statusClass(order)}`}>{statusLabel(order)}</span>
                  )}
                  {order.has_health_product && (
                    <span className="acct-docs-btn" onClick={e => { e.stopPropagation(); setDocsOrder(order); }} title="Reports / Documents" role="button" tabIndex={0}>
                      📄
                    </span>
                  )}
                  <span className="acct-order-amount">{formatPrice(order.order_amount)}</span>
                  <span className="acct-order-chevron">{expanded === order.id ? '▲' : '▼'}</span>
                </div>
              </button>

              {expanded === order.id && (
                <div className="acct-order-body">
                  {(order.delivery_date || order.delivery_window) && (
                    <div className="acct-delivery-info">
                      {order.delivery_date   && <span>&#128337; {formatDate(order.delivery_date)}</span>}
                      {order.delivery_window && <span>&#128336; {order.delivery_window}</span>}
                    </div>
                  )}
                  {order.lines.length > 0 ? (
                    <div className="acct-table-scroll">
                    <table className="acct-lines-table">
                      <thead>
                        <tr>
                          <th>Item</th>
                          <th className="acct-col-num">Qty</th>
                          <th className="acct-col-num">Unit Price</th>
                          <th className="acct-col-num">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {order.lines.map((line, i) => (
                          <React.Fragment key={i}>
                            <tr className={line.is_pack ? 'acct-pack-line' : ''}>
                              <td>{line.product_name}</td>
                              <td className="acct-col-num">{line.qty}</td>
                              <td className="acct-col-num">{formatPrice(line.unit_price)}</td>
                              <td className="acct-col-num">{formatPrice(line.total_price)}</td>
                            </tr>
                            {line.is_pack && line.selectionGroups && line.selectionGroups.flatMap((g, gi) =>
                              g.items.map((it, ii) => (
                                <tr key={`${i}-g${gi}-i${ii}`} className="acct-pack-sel-row">
                                  <td colSpan={4} className="acct-pack-sel-cell">
                                    <span className="acct-pack-sel-group">{g.name}:</span>{' '}
                                    {it.product_name}{' '}
                                    <span className="acct-pack-sel-qty">×{it.qty}</span>
                                  </td>
                                </tr>
                              ))
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td colSpan={3} className="acct-total-label">Order Total</td>
                          <td className="acct-col-num acct-total-value">{formatPrice(order.order_amount)}</td>
                        </tr>
                      </tfoot>
                    </table>
                    </div>
                  ) : (
                    <p className="acct-no-lines">No items on this order.</p>
                  )}
                </div>
              )}
            </div>
          );
        }

        // ── PMB/PUB parent card ──────────────────────────────
        if (item.type === 'pubpmb') {
          const parent = item.data;
          const isOpen = pubPmbExpanded.has(parent.parent_order_id);
          return (
            <div key={`pubpmb-${parent.parent_order_id}`} className="myk-parent-card">
              <div className="myk-parent-header" onClick={() => togglePubPmb(parent.parent_order_id)}>
                <div className="myk-parent-left">
                  <div>
                    <span className="myk-order-no">{parent.parent_order_no}</span>
                    <span className="myk-order-date">
                      {parent.pub_pmb === 'PMB' ? 'Personalised Meal Box' : 'Plan Your Box'}
                    </span>
                    {parent.children.length > 0 && (
                      <span className="myk-order-date">
                        {parent.children.length} delivery{parent.children.length !== 1 ? 's' : ''} · From {fmtDateMyk(parent.children[0].delivery_date)}
                      </span>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span className="myk-order-amount">₹{parseFloat(parent.parent_order_amount || 0).toFixed(0)} paid</span>
                  <span className="acct-order-chevron">{isOpen ? '▲' : '▼'}</span>
                </div>
              </div>
              {isOpen && (
                <div className="myk-kit-block">
                  <table className="myk-table">
                    <thead>
                      <tr><th>#</th><th>Date</th><th>Items</th><th>Status</th><th>Actions</th></tr>
                    </thead>
                    <tbody>
                      {parent.children.map((child, idx) => (
                        <tr key={child.id} className={child.order_lc_state === 'Cancelled' ? 'myk-row--cancelled' : ''}>
                          <td className="myk-seq">{idx + 1}</td>
                          <td><span className="myk-date">{fmtDateMyk(child.delivery_date)}</span></td>
                          <td className="myk-contents">
                            {child.items.map((it, i) => (
                              <span key={i} className="myk-item-chip myk-item-chip--sm">
                                {it.product_name} ×{it.qty}
                              </span>
                            ))}
                          </td>
                          <td>
                            <span className={`myk-state myk-state--${child.order_lc_state?.toLowerCase()}`}>
                              {STATE_ICON_MYK[child.order_lc_state] || ''} {child.order_lc_state}
                            </span>
                          </td>
                          <td>
                            {isActionablePubPmb(child) && (
                              <div className="myk-actions">
                                <button className="myk-btn myk-btn--reschedule"
                                  onClick={() => openReschedule(child, null, 'pubpmb')}>
                                  Reschedule
                                </button>
                                <button className="myk-btn myk-btn--cancel"
                                  onClick={() => openCancel({ ...child, pub_pmb: parent.pub_pmb }, child.items, 'pubpmb')}>
                                  Cancel
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        }

        // ── Kit parent card ──────────────────────────────────
        const parent = item.data;
        if (parent.pending) {
          return (
            <div key={`kit-${parent.parent_order_id}`} className="myk-parent-card myk-parent-card--pending">
              <div className="myk-pending-header">
                <div className="myk-parent-left">
                  <div>
                    <span className="myk-order-no">{parent.parent_order_no}</span>
                    <span className="myk-pending-badge">Payment Pending</span>
                  </div>
                  <span className="myk-order-date">{parent.kits.map(k => k.kit_name).join(', ')}</span>
                  {parent.parent_delivery_date && (
                    <span className="myk-order-date">
                      First delivery: {fmtDateMyk(parent.parent_delivery_date)}
                      {parent.parent_delivery_window && ` · ${parent.parent_delivery_window}`}
                    </span>
                  )}
                  <span className="myk-order-date">
                    {parent.kits.map(k => {
                      const dp = (k.delivery_days || []).map(d => DOW_MYK[d]).join(', ');
                      return `${k.item_days}-day kit · ${k.delivery_count} deliveries${dp ? ` · ${dp}` : ''}`;
                    }).join(' | ')}
                  </span>
                </div>
                <div className="myk-pending-right">
                  <span className="myk-order-amount">₹{parseFloat(parent.parent_order_amount || 0).toFixed(0)}</span>
                  <button className="myk-btn myk-btn--pay-now" onClick={() => setKitPayingOrder(parent)}>
                    Pay Now
                  </button>
                </div>
              </div>
            </div>
          );
        }

        const isOpen = kitExpanded.has(parent.parent_order_id);
        return (
          <div key={`kit-${parent.parent_order_id}`} className="myk-parent-card">
            <div className="myk-parent-header" onClick={() => toggleKit(parent.parent_order_id)}>
              <div className="myk-parent-left">
                <div>
                  <span className="myk-order-no">{parent.parent_order_no}</span>
                  <span className="myk-order-date">{parent.kits.map(k => k.kit_name).join(', ')}</span>
                  <span className="myk-order-date">
                    First delivery: {fmtDateMyk(
                      parent.kits.flatMap(k => k.deliveries.map(d => d.delivery_date))
                        .filter(Boolean).sort()[0]
                    )}
                  </span>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span className="myk-order-amount">₹{parseFloat(parent.parent_order_amount || 0).toFixed(0)} paid</span>
                <span className="acct-order-chevron">{isOpen ? '▲' : '▼'}</span>
              </div>
            </div>

            {isOpen && (
              <>
                {parent.non_kit_child && (
                  <div className="myk-kit-block">
                    <div className="myk-kit-name">🛒 Other Items</div>
                    <div className="myk-kit-meta">
                      <span>Delivery: {fmtDateMyk(parent.non_kit_child.delivery_date)}</span>
                    </div>
                    <table className="myk-table">
                      <thead><tr><th>Order No</th><th>Items</th><th>Status</th></tr></thead>
                      <tbody>
                        <tr className={parent.non_kit_child.order_state === 'Cancelled' ? 'myk-row--cancelled' : ''}>
                          <td className="myk-seq">{parent.non_kit_child.order_no}</td>
                          <td className="myk-contents">{parent.non_kit_child.items_summary}</td>
                          <td>
                            <span className={`myk-state myk-state--${parent.non_kit_child.order_state?.toLowerCase()}`}>
                              {STATE_ICON_MYK[parent.non_kit_child.order_state] || ''} {parent.non_kit_child.order_state}
                            </span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
                {parent.kits.map(kit => {
                  const delivered   = kit.deliveries.filter(d => d.order_state === 'Delivered').length;
                  const cancelled   = kit.deliveries.filter(d => d.order_state === 'Cancelled').length;
                  const upcoming    = kit.deliveries.filter(d => !['Delivered','Dispatched','Cancelled'].includes(d.order_state)).length;
                  const delivDayNames   = (kit.delivery_days    || []).map(d => DOW_MYK[d]).join(', ');
                  const consumpDayNames = (kit.consumption_days || []).map(d => DOW_MYK[d]).join(', ');
                  const multiplierMap   = itemsPerDelivery(kit.delivery_days, kit.consumption_days);
                  const variesQty       = Object.values(multiplierMap).some(v => v !== Object.values(multiplierMap)[0]);

                  return (
                    <div key={kit.kit_product_id} className="myk-kit-block">
                      <div className="myk-kit-name">{kit.kit_name}</div>
                      <div className="myk-kit-meta">
                        <span>{kit.item_days}-day kit · {kit.delivery_count} deliveries</span>
                        {consumpDayNames && <span>· consumed {consumpDayNames}</span>}
                        {delivDayNames   && <span>· delivered {delivDayNames}</span>}
                        <span>· ✅ {delivered} done</span>
                        {cancelled > 0 && <span>· ❌ {cancelled} cancelled</span>}
                        <span>· 📦 {upcoming} upcoming</span>
                      </div>
                      <div className="myk-kit-items-summary">
                        {variesQty ? 'Per serving:' : 'Each delivery:'}{' '}
                        {kit.kit_items.map((kitItem, i) => (
                          <span key={i} className="myk-item-chip">
                            {kitItem.product_name} ×{kitItem.quantity}
                          </span>
                        ))}
                        {variesQty && (
                          <span style={{ color: '#888', fontSize: '0.76rem' }}>(qty varies by delivery day)</span>
                        )}
                      </div>
                      <table className="myk-table">
                        <thead>
                          <tr><th>#</th><th>Date</th><th>Contents</th><th>Status</th><th>Actions</th></tr>
                        </thead>
                        <tbody>
                          {kit.deliveries.map(d => {
                            const mult = d.delivery_date ? (multiplierMap[dowOf(d.delivery_date)] ?? 1) : 1;
                            const deliveryItems = d.items?.length > 0
                              ? d.items
                              : kit.kit_items.map(ki => ({ product_name: ki.product_name, qty: ki.quantity * mult }));
                            return (
                              <tr key={d.id} className={d.order_state === 'Cancelled' ? 'myk-row--cancelled' : ''}>
                                <td className="myk-seq">{d.sequence_no}</td>
                                <td>
                                  <span className="myk-date">{fmtDateMyk(d.delivery_date)}</span>
                                  {d.original_delivery_date && d.original_delivery_date !== d.delivery_date && (
                                    <span className="myk-original-date">orig: {fmtDateMyk(d.original_delivery_date)}</span>
                                  )}
                                </td>
                                <td className="myk-contents">
                                  {deliveryItems.map((di, i) => (
                                    <span key={i} className="myk-item-chip myk-item-chip--sm">
                                      {di.product_name} ×{di.qty}
                                    </span>
                                  ))}
                                </td>
                                <td>
                                  <span className={`myk-state myk-state--${d.order_state?.toLowerCase()}`}>
                                    {STATE_ICON_MYK[d.order_state] || ''} {d.order_state}
                                  </span>
                                </td>
                                <td>
                                  {isActionable(d) && (
                                    <div className="myk-actions">
                                      <button className="myk-btn myk-btn--reschedule"
                                        onClick={() => openReschedule(d, kit.delivery_days)}>
                                        Reschedule
                                      </button>
                                      <button className="myk-btn myk-btn--cancel"
                                        onClick={() => openCancel(d, deliveryItems)}>
                                        Cancel
                                      </button>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        );
      })}

      {totalPages > 1 && (
        <div className="acct-pagination">
          <button className="acct-page-btn"
            onClick={() => { setPage(p => p - 1); setExpanded(null); }}
            disabled={page === 0}>
            &#8592;
          </button>
          <span className="acct-page-info">{page + 1} / {totalPages}</span>
          <button className="acct-page-btn"
            onClick={() => { setPage(p => p + 1); setExpanded(null); }}
            disabled={page === totalPages - 1}>
            &#8594;
          </button>
        </div>
      )}

      {docsOrder && (
        <HealthDocsModal
          order={docsOrder}
          user={user}
          onClose={() => setDocsOrder(null)}
        />
      )}

      {payingOrder && (
        <PaymentModal
          order={payingOrder}
          user={user}
          onClose={() => setPayingOrder(null)}
          onPaid={(orderId) => {
            setAllItems(prev => prev.map(item =>
              item.type === 'regular' && item.data.id === orderId
                ? { ...item, data: { ...item.data, order_lc_state: 'Placed' } }
                : item
            ));
            setPayingOrder(null);
          }}
        />
      )}

      {kitPayingOrder && (
        <KitPaymentModal
          order={kitPayingOrder}
          user={user}
          onClose={() => setKitPayingOrder(null)}
          onPaid={() => { setKitPayingOrder(null); load(); }}
        />
      )}

      {rescheduleModal && (
        <div className="myk-modal-overlay" onClick={() => setRescheduleModal(null)}>
          <div className="myk-modal" onClick={e => e.stopPropagation()}>
            <div className="myk-modal-header">
              <h3>Reschedule Delivery{rescheduleModal.delivery.sequence_no ? ` #${rescheduleModal.delivery.sequence_no}` : ''}</h3>
              <button className="myk-modal-close" onClick={() => setRescheduleModal(null)}>×</button>
            </div>
            <div className="myk-modal-body">
              <p className="myk-modal-current">
                Current date: <strong>{fmtDateMyk(rescheduleModal.delivery.delivery_date)}</strong>
              </p>
              {rescheduleError && <p className="myk-error">{rescheduleError}</p>}
              {rescheduleModal.modalType === 'pubpmb' ? (() => {
                const tomorrow = new Date();
                tomorrow.setDate(tomorrow.getDate() + 1);
                const minDate = tomorrow.toISOString().split('T')[0];
                const maxDate = new Date(tomorrow.getTime() + 59 * 86400000).toISOString().split('T')[0];
                const dateOutOfRange = pickedDate && (pickedDate < minDate || pickedDate > maxDate);
                return (
                  <>
                    <p className="myk-modal-sub">Select a new delivery date (any day, within 60 days):</p>
                    <input
                      type="date"
                      className="myk-date-input"
                      min={minDate}
                      max={maxDate}
                      value={pickedDate}
                      onChange={e => setPickedDate(e.target.value)}
                      disabled={rescheduleLoading}
                    />
                    {dateOutOfRange && (
                      <p className="myk-error" style={{ marginTop: '0.4rem' }}>
                        Please pick a date between {fmtDateMyk(minDate)} and {fmtDateMyk(maxDate)}.
                      </p>
                    )}
                    <div className="myk-modal-footer">
                      <button className="myk-btn myk-btn--back" onClick={() => setRescheduleModal(null)} disabled={rescheduleLoading}>
                        Cancel
                      </button>
                      <button
                        className="myk-btn myk-btn--pay"
                        disabled={rescheduleLoading || !pickedDate || dateOutOfRange}
                        onClick={() => confirmReschedule(pickedDate)}
                      >
                        {rescheduleLoading ? 'Saving…' : 'Reschedule'}
                      </button>
                    </div>
                  </>
                );
              })() : (
                <>
                  <p className="myk-modal-sub">
                    Available dates ({(rescheduleModal.scheduleDays || []).map(d => DOW_MYK[d]).join(', ') || 'any delivery day'}, within 60 days):
                  </p>
                  {validDates.length === 0 && !rescheduleError && (
                    <p className="myk-empty">No available dates found.</p>
                  )}
                  <div className="myk-date-grid">
                    {validDates.map(date => (
                      <button key={date} className="myk-date-pill"
                        disabled={rescheduleLoading} onClick={() => confirmReschedule(date)}>
                        {fmtDateMyk(date)}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {cancelModal && (
        <div className="myk-modal-overlay" onClick={() => setCancelModal(null)}>
          <div className="myk-modal" onClick={e => e.stopPropagation()}>
            <div className="myk-modal-header">
              <h3>Cancel Delivery{cancelModal.delivery.sequence_no ? ` #${cancelModal.delivery.sequence_no}` : ''}?</h3>
              <button className="myk-modal-close" onClick={() => setCancelModal(null)}>×</button>
            </div>
            <div className="myk-modal-body">
              <p>You are cancelling the delivery of:</p>
              <ul className="myk-cancel-items">
                {cancelModal.kitItems.map((ci, i) => (
                  <li key={i}>{ci.product_name} ×{ci.qty ?? ci.quantity}</li>
                ))}
              </ul>
              <p>scheduled for <strong>{fmtDateMyk(cancelModal.delivery.delivery_date)}</strong>.</p>
              {cancelModal.modalType === 'pubpmb' ? (
                <div className="myk-cancel-warning">
                  <p>⚠ WARNING: This delivery CANNOT be refunded.</p>
                  <p>You will permanently lose this delivery. This cannot be undone.</p>
                </div>
              ) : (
                <div className="myk-cancel-warning">
                  <p>⚠ WARNING: This delivery CANNOT be refunded or replaced.</p>
                  <p>You will permanently lose this delivery from your kit. This cannot be undone.</p>
                </div>
              )}
              <label className="myk-cancel-label">Type <strong>CANCEL</strong> to confirm:</label>
              <input type="text" className="myk-cancel-input" value={cancelConfirmText}
                onChange={e => setCancelConfirmText(e.target.value)}
                placeholder="Type CANCEL here" autoComplete="off" />
              {cancelError && <p className="myk-error">{cancelError}</p>}
              <div className="myk-modal-footer">
                <button className="myk-btn myk-btn--back" onClick={() => setCancelModal(null)}>Go Back</button>
                <button className="myk-btn myk-btn--confirm-cancel" onClick={confirmCancel}
                  disabled={cancelLoading || cancelConfirmText !== 'CANCEL'}>
                  {cancelLoading ? 'Cancelling…' : 'Permanently Cancel Delivery'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Change Password modal ──────────────────────────────────
const ChangePasswordModal = ({ user, onClose }) => {
  const { refreshUser } = useContext(AuthContext);
  const [form, setForm] = useState({ current: '', next: '', confirm: '' });
  const [phase, setPhase] = useState('idle'); // idle | loading | success
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    setError('');
    if (!form.current) { setError('Enter your current password.'); return; }
    if (form.next.length < 4) { setError('New password must be at least 4 characters.'); return; }
    if (form.next !== form.confirm) { setError('New passwords do not match.'); return; }

    setPhase('loading');
    try {
      await axios.post(
        `${API_URL}/authRoutes/change-password`,
        { current_password: form.current, new_password: form.next },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      await refreshUser();
      setPhase('success');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not change password. Please try again.');
      setPhase('idle');
    }
  };

  return (
    <div className="acct-pay-overlay" onClick={() => phase !== 'loading' && onClose()}>
      <div className="acct-pay-modal" onClick={e => e.stopPropagation()}>
        <div className="acct-pay-header">
          <h3>Change Password</h3>
          <button className="acct-pay-close" onClick={onClose} disabled={phase === 'loading'}>&#215;</button>
        </div>

        <div className="acct-pay-body">
          {phase === 'success' ? (
            <p style={{ textAlign: 'center', color: '#27ae60', fontWeight: 600, padding: '8px 0' }}>
              Password changed successfully.
            </p>
          ) : (
            <>
              {[
                { key: 'current', label: 'Current Password' },
                { key: 'next',    label: 'New Password' },
                { key: 'confirm', label: 'Confirm New Password' },
              ].map(({ key, label }) => (
                <div key={key} className="acct-chpw-field">
                  <label className="acct-chpw-label">{label}</label>
                  <input
                    type="password"
                    className="acct-chpw-input"
                    value={form[key]}
                    onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                    onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                    autoComplete="new-password"
                  />
                </div>
              ))}
              {error && <p className="acct-pay-error">{error}</p>}
            </>
          )}
        </div>

        <div className="acct-pay-footer">
          <button className="acct-pay-cancel-btn" onClick={onClose} disabled={phase === 'loading'}>
            {phase === 'success' ? 'Close' : 'Cancel'}
          </button>
          {phase !== 'success' && (
            <button
              className="acct-pay-btn"
              onClick={handleSubmit}
              disabled={phase === 'loading'}
            >
              {phase === 'loading' ? 'Changing…' : 'Change Password'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Profile Details Modal ─────────────────────────────────
const fmtMMDD = (s) => {
  if (!s || s === '00-00') return '—';
  const [mm, dd] = s.split('-');
  if (!mm || !dd || mm === '00' || dd === '00') return '—';
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${parseInt(dd, 10)} ${months[parseInt(mm, 10) - 1] || ''}`;
};

const fmtOccDate = (s) => {
  if (!s) return '—';
  return new Date(s).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

const ProfileDetailsModal = ({ user, onClose }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/user-profile/me`, {
      headers: { Authorization: `Bearer ${user.token}` },
    }).then(res => { setData(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [user.token]); // eslint-disable-line

  const handleEdit = () => {
    onClose();
    navigate('/know-you-better?edit=true');
  };

  const profile = data?.profile;
  const family = data?.family || [];
  const occasions = data?.occasions || [];

  const MEMBER_LABEL = { SPOUSE: '💑 Spouse', PARENT: '👨‍👩‍ Parent', KID: '👶 Child' };

  const Row = ({ label, value }) => value && value !== '—' ? (
    <div style={{ display:'flex', justifyContent:'space-between', padding:'6px 0', borderBottom:'1px solid #F5F0E8', fontSize:'14px' }}>
      <span style={{ color:'#8B6914', fontWeight:600, minWidth:'140px' }}>{label}</span>
      <span style={{ color:'#333', textAlign:'right', flex:1 }}>{value}</span>
    </div>
  ) : null;

  return (
    <div className="acct-pay-overlay" onClick={onClose}>
      <div className="acct-pay-modal" style={{ maxWidth:'520px', maxHeight:'85vh', display:'flex', flexDirection:'column' }} onClick={e => e.stopPropagation()}>
        <div className="acct-pay-header">
          <h3>My Profile Details</h3>
          <button className="acct-pay-close" onClick={onClose}>&#215;</button>
        </div>

        <div className="acct-pay-body" style={{ overflowY:'auto', flex:1 }}>
          {loading ? (
            <p style={{ textAlign:'center', color:'#9B7222', padding:'20px 0' }}>Loading…</p>
          ) : !profile && occasions.length === 0 && family.length === 0 ? (
            <p style={{ textAlign:'center', color:'#aaa', padding:'20px 0' }}>No profile details saved yet.</p>
          ) : (
            <>
              {/* About You */}
              {profile && (
                <div style={{ marginBottom:'18px' }}>
                  <div style={{ fontWeight:700, color:'#5C3D1E', fontSize:'13px', textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:'8px' }}>About You</div>
                  <Row label="Date of Birth"       value={fmtMMDD(profile.dob)} />
                  <Row label="Wedding Anniversary" value={fmtMMDD(profile.wedding_anniversary)} />
                  <Row label="Family Size"         value={profile.family_size} />
                  <Row label="Favourite Food"      value={profile.favourite_food} />
                  <Row label="Favourite Meat"      value={profile.favourite_meat} />
                  <Row label="Allergies"           value={profile.allergies} />
                  {profile.food_preferences?.length > 0 && (
                    <div style={{ display:'flex', justifyContent:'space-between', padding:'6px 0', borderBottom:'1px solid #F5F0E8', fontSize:'14px' }}>
                      <span style={{ color:'#8B6914', fontWeight:600, minWidth:'140px' }}>Food Preferences</span>
                      <div style={{ display:'flex', flexWrap:'wrap', gap:'5px', justifyContent:'flex-end', flex:1 }}>
                        {profile.food_preferences.map(p => (
                          <span key={p} style={{ background:'#FFF3CD', color:'#7A5C2E', borderRadius:'12px', padding:'2px 10px', fontSize:'12px', fontWeight:600 }}>{p}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Special Occasions */}
              {occasions.length > 0 && (
                <div style={{ marginBottom:'18px' }}>
                  <div style={{ fontWeight:700, color:'#5C3D1E', fontSize:'13px', textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:'8px' }}>Special Occasions</div>
                  {occasions.map((o, i) => (
                    <Row key={i} label={o.occasion_name} value={fmtOccDate(o.occasion_date)} />
                  ))}
                </div>
              )}

              {/* Family */}
              {family.length > 0 && (
                <div>
                  <div style={{ fontWeight:700, color:'#5C3D1E', fontSize:'13px', textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:'8px' }}>Family</div>
                  {family.map((m, i) => (
                    <div key={i} style={{ background:'#FFFDF5', border:'1px solid #EDD98A', borderRadius:'10px', padding:'12px 14px', marginBottom:'10px' }}>
                      <div style={{ fontWeight:700, color:'#7A5C2E', fontSize:'13px', marginBottom:'8px' }}>
                        {MEMBER_LABEL[m.member_type] || m.member_type}
                        {m.name ? ` — ${m.name}` : ''}
                      </div>
                      <Row label="Date of Birth"       value={fmtMMDD(m.dob)} />
                      <Row label="Wedding Anniversary" value={fmtMMDD(m.wedding_anniversary)} />
                      {m.occasions?.map((o, j) => (
                        <Row key={j} label={o.occasion_name} value={fmtOccDate(o.occasion_date)} />
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <div className="acct-pay-footer">
          <button className="acct-pay-cancel-btn" onClick={onClose}>Close</button>
          <button className="acct-pay-btn" onClick={handleEdit}>Edit</button>
        </div>
      </div>
    </div>
  );
};

// ── Address management section (used inside PersonalDetailsTab) ──────────────
const AddressBook = ({ user }) => {
  const { refreshDeliveryConfig } = useContext(AuthContext);
  const auth = { Authorization: `Bearer ${user.token}` };
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [mode, setMode]           = useState(null); // null | 'add-private' | 'add-shared' | 'edit'
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm]           = useState({ label: 'Home', address: '', pincode: '', notes: '' });
  const [sharedQuery, setSharedQuery] = useState('');
  const [sharedResults, setSharedResults] = useState([]);
  const [saving, setSaving]       = useState(false);
  const [formError, setFormError] = useState('');

  const load = () => {
    if (!user?.customer_id) return;
    setLoading(true);
    axios.get(`${API_URL}/addresses/my-addresses`, { headers: auth })
      .then(r => { setAddresses(r.data); setLoading(false); })
      .catch(() => { setError('Could not load addresses.'); setLoading(false); });
  };
  useEffect(load, [user]); // eslint-disable-line

  const searchShared = async (q) => {
    setSharedQuery(q);
    if (!q.trim()) { setSharedResults([]); return; }
    try {
      const r = await axios.get(`${API_URL}/addresses/shared/search`, { params: { q }, headers: auth });
      setSharedResults(r.data);
    } catch { setSharedResults([]); }
  };

  const startAddPrivate = () => {
    setForm({ label: 'Home', address: '', pincode: '', notes: '' });
    setFormError(''); setMode('add-private');
  };
  const startAddShared = () => {
    setSharedQuery(''); setSharedResults([]);
    setFormError(''); setMode('add-shared');
  };
  const startEdit = (addr) => {
    setEditTarget(addr);
    setForm({ label: addr.label, address: addr.address, pincode: addr.pincode, notes: addr.notes || '' });
    setFormError(''); setMode('edit');
  };
  const cancel = () => { setMode(null); setEditTarget(null); setFormError(''); };

  const savePrivate = async () => {
    if (!form.address.trim() || !form.pincode.trim()) {
      setFormError('Address and pincode are required.'); return;
    }
    setSaving(true); setFormError('');
    try {
      await axios.post(`${API_URL}/addresses/my-addresses`,
        { label: form.label, address: form.address, pincode: form.pincode, notes: form.notes },
        { headers: auth }
      );
      cancel(); load();
    } catch (err) {
      setFormError(err.response?.data?.error || 'Failed to save.');
    } finally { setSaving(false); }
  };

  const linkShared = async (shared) => {
    setSaving(true); setFormError('');
    try {
      await axios.post(`${API_URL}/addresses/my-addresses`,
        { label: form.label || 'Office', shared_address_id: shared.id, notes: form.notes },
        { headers: auth }
      );
      cancel(); load();
    } catch (err) {
      setFormError(err.response?.data?.error || 'Failed to link address.');
    } finally { setSaving(false); }
  };

  const saveEdit = async () => {
    setSaving(true); setFormError('');
    try {
      const body = { label: form.label, notes: form.notes };
      if (!editTarget.is_shared) { body.address = form.address; body.pincode = form.pincode; }
      await axios.put(`${API_URL}/addresses/my-addresses/${editTarget.id}`, body, { headers: auth });
      cancel(); load();
    } catch (err) {
      setFormError(err.response?.data?.error || 'Failed to save.');
    } finally { setSaving(false); }
  };

  const setDefault = async (addr) => {
    try {
      await axios.put(`${API_URL}/addresses/my-addresses/${addr.id}/default`, {}, { headers: auth });
      load();
      refreshDeliveryConfig();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to set default.');
    }
  };

  const remove = async (addr) => {
    if (!window.confirm('Remove this address?')) return;
    try {
      await axios.delete(`${API_URL}/addresses/my-addresses/${addr.id}`, { headers: auth });
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove address.');
    }
  };

  if (loading) return <p className="acct-loading">Loading addresses…</p>;

  return (
    <div className="acct-addr-book">
      <div className="acct-addr-header">
        <span className="acct-addr-title">Delivery Addresses</span>
        <div className="acct-addr-add-btns">
          <button className="acct-addr-add-btn" onClick={startAddPrivate}>+ Private</button>
          <button className="acct-addr-add-btn" onClick={startAddShared}>+ Shared Location</button>
        </div>
      </div>

      {error && <p className="acct-pay-error">{error}</p>}

      {/* Address cards */}
      {addresses.length === 0 && !mode && (
        <p className="acct-addr-empty">No addresses saved yet.</p>
      )}
      <div className="acct-addr-cards">
        {addresses.map(addr => (
          <div key={addr.id} className={`acct-addr-card${addr.is_default ? ' acct-addr-card--default' : ''}`}>
            <div className="acct-addr-card-top">
              <span className="acct-addr-label">{addr.label}</span>
              {addr.is_shared && <span className="acct-addr-badge acct-addr-badge--shared">Shared</span>}
              {addr.waive_delivery_fee && <span className="acct-addr-badge acct-addr-badge--free">Free Delivery</span>}
              {addr.is_default && <span className="acct-addr-badge acct-addr-badge--default">Default</span>}
            </div>
            {addr.is_shared && addr.location_name && (
              <div className="acct-addr-location">{addr.location_name}</div>
            )}
            <div className="acct-addr-text">{addr.address}</div>
            <div className="acct-addr-pincode">{addr.pincode}</div>
            {addr.notes && <div className="acct-addr-notes">{addr.notes}</div>}
            <div className="acct-addr-actions">
              {!addr.is_default && (
                <button className="acct-addr-action-btn" onClick={() => setDefault(addr)}>Set Default</button>
              )}
              <button className="acct-addr-action-btn" onClick={() => startEdit(addr)}>Edit</button>
              <button className="acct-addr-action-btn acct-addr-action-btn--remove" onClick={() => remove(addr)} disabled={addresses.length === 1}>Remove</button>
            </div>
          </div>
        ))}
      </div>

      {/* Add / edit form */}
      {mode && (
        <div className="acct-addr-form-wrap">
          {formError && <p className="acct-pay-error" style={{ marginBottom: 8 }}>{formError}</p>}

          <div className="acct-edit-field">
            <label className="acct-edit-label">Label</label>
            <input className="acct-edit-input" value={form.label}
              onChange={e => setForm(f => ({ ...f, label: e.target.value }))} disabled={saving} />
          </div>

          {mode === 'add-shared' && (
            <>
              <div className="acct-edit-field">
                <label className="acct-edit-label">Search shared location</label>
                <input className="acct-edit-input" placeholder="Type name or area…"
                  value={sharedQuery} onChange={e => searchShared(e.target.value)} disabled={saving} />
              </div>
              {sharedResults.length > 0 && (
                <div className="acct-addr-shared-results">
                  {sharedResults.map(s => (
                    <div key={s.id} className="acct-addr-shared-result" onClick={() => linkShared(s)}>
                      <strong>{s.name}</strong>
                      <span>{s.address} — {s.pincode}</span>
                      {s.waive_delivery_fee && <span className="acct-addr-badge acct-addr-badge--free">Free Delivery</span>}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {(mode === 'add-private' || (mode === 'edit' && !editTarget?.is_shared)) && (
            <>
              <div className="acct-edit-field">
                <label className="acct-edit-label">Address</label>
                <textarea className="acct-edit-input acct-edit-textarea" rows={3}
                  value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
                  disabled={saving} />
              </div>
              <div className="acct-edit-field">
                <label className="acct-edit-label">Pincode</label>
                <input className="acct-edit-input" value={form.pincode} maxLength={6}
                  onChange={e => setForm(f => ({ ...f, pincode: e.target.value }))} disabled={saving} />
              </div>
            </>
          )}

          <div className="acct-edit-field">
            <label className="acct-edit-label">Delivery Notes (optional)</label>
            <input className="acct-edit-input" placeholder="e.g. Ring bell, 2nd floor"
              value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              disabled={saving} />
          </div>

          <div className="acct-edit-actions">
            <button className="acct-pay-cancel-btn" onClick={cancel} disabled={saving}>Cancel</button>
            {mode === 'add-private' && (
              <button className="acct-pay-btn" onClick={savePrivate} disabled={saving}>
                {saving ? 'Saving…' : 'Add Address'}
              </button>
            )}
            {mode === 'edit' && (
              <button className="acct-pay-btn" onClick={saveEdit} disabled={saving}>
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Switch User section (admin.switch.user permission only) ──
const SwitchUserSection = ({ user }) => {
  const { switchUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const auth = { Authorization: `Bearer ${user.token}` };

  const [query, setQuery]       = useState('');
  const [results, setResults]   = useState([]);
  const [selected, setSelected] = useState(null);
  const [switching, setSwitching] = useState(false);
  const [error, setError]       = useState('');

  const search = async (q) => {
    setQuery(q);
    setSelected(null);
    if (!q.trim()) { setResults([]); return; }
    try {
      const res = await axios.get(`${API_URL}/authRoutes/users/search`, {
        params: { q }, headers: auth,
      });
      setResults(res.data);
    } catch { setResults([]); }
  };

  const handleSwitch = async () => {
    if (!selected) return;
    setSwitching(true); setError('');
    try {
      const res = await axios.post(`${API_URL}/authRoutes/impersonate`,
        { target_user_id: selected.id }, { headers: auth }
      );
      switchUser(res.data.token);
      navigate('/home', { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to switch user.');
      setSwitching(false);
    }
  };

  return (
    <div className="acct-switch-user-section">
      <div className="acct-switch-user-title">Switch User</div>
      <div className="acct-edit-field">
        <label className="acct-edit-label">Search by name or phone</label>
        <input
          className="acct-edit-input"
          value={query}
          onChange={e => search(e.target.value)}
          placeholder="Type to search…"
          disabled={switching}
        />
      </div>
      {results.length > 0 && !selected && (
        <div className="acct-switch-user-results">
          {results.map(u => (
            <div
              key={u.id}
              className="acct-switch-user-result"
              onClick={() => { setSelected(u); setResults([]); setQuery(u.name); }}
            >
              <strong>{u.name}</strong>
              {u.phone && <span> · {u.phone}</span>}
              {u.user_type && <span className="acct-switch-user-type"> [{u.user_type}]</span>}
            </div>
          ))}
        </div>
      )}
      {selected && (
        <div className="acct-switch-user-confirm">
          <p>Switch to <strong>{selected.name}</strong>{selected.phone ? ` (${selected.phone})` : ''}?</p>
          <p className="acct-switch-user-warn">You will be signed out and in as this user. There is no switch back.</p>
          <div className="acct-edit-actions">
            <button
              className="acct-pay-cancel-btn"
              onClick={() => { setSelected(null); setQuery(''); }}
              disabled={switching}
            >
              Cancel
            </button>
            <button
              className="acct-switch-user-btn"
              onClick={handleSwitch}
              disabled={switching}
            >
              {switching ? 'Switching…' : `Switch to ${selected.name}`}
            </button>
          </div>
        </div>
      )}
      {error && <p className="acct-pay-error">{error}</p>}
    </div>
  );
};

// ── Personal Details tab ──────────────────────────────────
const PersonalDetailsTab = ({ user }) => {
  const { refreshUser } = useContext(AuthContext);
  const [showChPw, setShowChPw] = useState(false);
  const [showProfile, setShowProfile] = useState(false);

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: '', email: '' });
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState('');

  const auth = { Authorization: `Bearer ${user.token}` };

  const fmtDate = (s) => {
    if (!s) return '—';
    return new Date(s).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const startEdit = () => {
    setSaveError(''); setSaveSuccess('');
    setForm({ name: user?.name || '', email: user?.email || '' });
    setEditing(true);
  };

  const cancelEdit = () => { setEditing(false); setSaveError(''); };

  const handleSave = async () => {
    setSaveError(''); setSaveSuccess('');
    setSaveLoading(true);
    try {
      await axios.put(`${API_URL}/authRoutes/me`,
        { name: form.name, email: form.email },
        { headers: auth }
      );
      await refreshUser();
      setSaveSuccess('Details updated successfully.');
      setEditing(false);
    } catch (err) {
      setSaveError(err.response?.data?.error || 'Failed to save. Please try again.');
    } finally {
      setSaveLoading(false);
    }
  };

  const rows = [
    { label: 'Full Name',    value: user?.name },
    { label: 'Email',        value: user?.email },
    { label: 'Phone',        value: user?.phone },
    { label: 'Member Since', value: fmtDate(user?.created_date) },
    ...(user?.updated_date ? [{ label: 'Last Updated', value: fmtDate(user.updated_date) }] : []),
  ];

  return (
    <>
      <div>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginBottom: '12px' }}>
          <button className="acct-myprofile-btn" onClick={() => setShowProfile(true)}>
            My Profile
          </button>
          {!editing && (
            <button className="acct-myprofile-btn" onClick={startEdit}>
              Edit Details
            </button>
          )}
        </div>

        {saveSuccess && (
          <div className="acct-save-success">{saveSuccess}</div>
        )}

        {editing ? (
          <div className="acct-edit-form">
            {saveError && <p className="acct-pay-error" style={{ marginBottom: 10 }}>{saveError}</p>}

            {[
              { key: 'name',  label: 'Full Name', type: 'text' },
              { key: 'email', label: 'Email',     type: 'email' },
            ].map(({ key, label, type }) => (
              <div key={key} className="acct-edit-field">
                <label className="acct-edit-label">{label}</label>
                <input
                  type={type}
                  className="acct-edit-input"
                  value={form[key]}
                  onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                  disabled={saveLoading}
                />
              </div>
            ))}

            <div className="acct-edit-actions">
              <button className="acct-pay-cancel-btn" onClick={cancelEdit} disabled={saveLoading}>
                Cancel
              </button>
              <button className="acct-pay-btn" onClick={handleSave} disabled={saveLoading}>
                {saveLoading ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        ) : (
          <div className="acct-details">
            {rows.map(({ label, value }) => (
              <div key={label} className="acct-detail-row">
                <span className="acct-detail-label">{label}</span>
                <span className="acct-detail-value">{value || '—'}</span>
              </div>
            ))}
            <div className="acct-detail-row acct-chpw-row">
              <span className="acct-detail-label">Password</span>
              <button className="acct-chpw-btn" onClick={() => setShowChPw(true)}>
                Change Password
              </button>
            </div>
          </div>
        )}

        {user?.customer_id && !editing && (
          <AddressBook user={user} />
        )}

        {!editing && user?.permissions?.some(p => p.name === 'admin.switch.user') && (
          <SwitchUserSection user={user} />
        )}
      </div>

      {showChPw && (
        <ChangePasswordModal user={user} onClose={() => setShowChPw(false)} />
      )}
      {showProfile && (
        <ProfileDetailsModal user={user} onClose={() => setShowProfile(false)} />
      )}
    </>
  );
};

// ── Referrals > Customers sub-tab ────────────────────────
const ReferredCustomersTab = ({ user }) => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);

  useEffect(() => {
    axios
      .get(`${API_URL}/menuRoutes/referred-customers`, {
        headers: { Authorization: `Bearer ${user.token}` },
      })
      .then(res => { setCustomers(res.data); setLoading(false); })
      .catch(() => { setError('Could not load referred customers.'); setLoading(false); });
  }, [user.token]);

  if (loading) return <p className="acct-loading">Loading customers…</p>;
  if (error)   return <p className="acct-error">{error}</p>;
  if (customers.length === 0) return (
    <div className="acct-empty"><p>No referred customers yet.</p></div>
  );

  return (
    <div className="acct-ref-customers">
      {customers.map(c => (
        <div key={c.id} className="acct-ref-customer-card">
          <div className="acct-ref-customer-name">{c.name || '—'}</div>
          <div className="acct-ref-customer-meta">
            {c.phone && <span>&#128222; {c.phone}</span>}
            {c.email && <span>&#9993; {c.email}</span>}
          </div>
          {c.address && (
            <div className="acct-ref-customer-address">&#128205; {c.address}</div>
          )}
        </div>
      ))}
    </div>
  );
};

// ── Referrals > Orders sub-tab ────────────────────────────
const ReferralOrdersSubTab = ({ user }) => {
  const [orders, setOrders]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [page, setPage]       = useState(0);

  useEffect(() => {
    axios
      .get(`${API_URL}/menuRoutes/referral-orders`, {
        headers: { Authorization: `Bearer ${user.token}` },
      })
      .then(res => { setOrders(res.data); setLoading(false); })
      .catch(() => { setError('Could not load referral orders.'); setLoading(false); });
  }, [user.token]);

  if (loading) return <p className="acct-loading">Loading referral orders…</p>;
  if (error)   return <p className="acct-error">{error}</p>;
  if (orders.length === 0) return (
    <div className="acct-empty"><p>No orders from referred customers yet.</p></div>
  );

  const totalPages = Math.ceil(orders.length / PAGE_SIZE);
  const pageOrders = orders.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  return (
    <div className="acct-orders">
      {pageOrders.map((order) => (
        <div key={order.id} className="acct-order-card">
          <button
            className="acct-order-header"
            onClick={() => setExpanded(expanded === order.id ? null : order.id)}
          >
            <div className="acct-order-meta">
              <span className="acct-order-no">{order.order_no}</span>
              <span className="acct-order-customer">{order.customer_name}</span>
              <span className="acct-order-date">{formatDate(order.order_date)}</span>
            </div>
            <div className="acct-order-right">
              <span className={`acct-order-status ${statusClass(order)}`}>
                {statusLabel(order)}
              </span>
              <span className="acct-order-amount">{formatPrice(order.order_amount)}</span>
              <span className="acct-order-chevron">{expanded === order.id ? '▲' : '▼'}</span>
            </div>
          </button>

          {expanded === order.id && (
            <div className="acct-order-body">
              {(order.delivery_date || order.delivery_window) && (
                <div className="acct-delivery-info">
                  {order.delivery_date && <span>&#128337; {formatDate(order.delivery_date)}</span>}
                  {order.delivery_window && <span>&#128336; {order.delivery_window}</span>}
                </div>
              )}
              {order.lines.length > 0 ? (
                <div className="acct-table-scroll">
                <table className="acct-lines-table">
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th className="acct-col-num">Qty</th>
                      <th className="acct-col-num">Unit Price</th>
                      <th className="acct-col-num">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.lines.map((line, i) => (
                      <React.Fragment key={i}>
                        <tr className={line.is_pack ? 'acct-pack-line' : ''}>
                          <td>{line.product_name}</td>
                          <td className="acct-col-num">{line.qty}</td>
                          <td className="acct-col-num">{formatPrice(line.unit_price)}</td>
                          <td className="acct-col-num">{formatPrice(line.total_price)}</td>
                        </tr>
                        {line.is_pack && line.selectionGroups && line.selectionGroups.flatMap((g, gi) =>
                          g.items.map((it, ii) => (
                            <tr key={`${i}-g${gi}-i${ii}`} className="acct-pack-sel-row">
                              <td colSpan={4} className="acct-pack-sel-cell">
                                <span className="acct-pack-sel-group">{g.name}:</span>{' '}
                                {it.product_name}{' '}
                                <span className="acct-pack-sel-qty">×{it.qty}</span>
                              </td>
                            </tr>
                          ))
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={3} className="acct-total-label">Order Total</td>
                      <td className="acct-col-num acct-total-value">{formatPrice(order.order_amount)}</td>
                    </tr>
                  </tfoot>
                </table>
                </div>
              ) : (
                <p className="acct-no-lines">No items on this order.</p>
              )}
            </div>
          )}
        </div>
      ))}
      {totalPages > 1 && (
        <div className="acct-pagination">
          <button className="acct-page-btn"
            onClick={() => { setPage(p => p - 1); setExpanded(null); }}
            disabled={page === 0}>&#8592;</button>
          <span className="acct-page-info">{page + 1} / {totalPages}</span>
          <button className="acct-page-btn"
            onClick={() => { setPage(p => p + 1); setExpanded(null); }}
            disabled={page === totalPages - 1}>&#8594;</button>
        </div>
      )}
    </div>
  );
};

// ── Stocks tab (store partner only) ──────────────────────
const StocksTab = ({ user }) => {
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const auth = { Authorization: `Bearer ${user?.token}` };

  useEffect(() => {
    axios.get(`${API_URL}/stock/my-balance`, { headers: auth })
      .then(r => { setStocks(r.data); setLoading(false); })
      .catch(() => { setError('Failed to load stock'); setLoading(false); });
  }, []); // eslint-disable-line

  if (loading) return <div className="acct-loading">Loading…</div>;
  if (error)   return <div className="acct-error">{error}</div>;
  if (!stocks.length) return <div className="acct-no-lines">No stock on hand.</div>;

  return (
    <div className="stock-card-grid">
      {stocks.map(s => (
        <div key={s.product_id} className="stock-card">
          <p className="stock-card-name">{s.product_name}</p>
          <p className="stock-card-qty">{Math.floor(parseFloat(s.qty_on_hand))}</p>
          <p className="stock-card-label">Qty on Hand</p>
        </div>
      ))}
    </div>
  );
};

// ── Referrals tab (wrapper with Customers / Orders sub-tabs)
const ReferralsTab = ({ user }) => {
  const [subTab, setSubTab] = useState('customers');
  return (
    <div>
      <div className="acct-subtabs">
        <button
          className={`acct-subtab${subTab === 'customers' ? ' acct-subtab--active' : ''}`}
          onClick={() => setSubTab('customers')}
        >
          Customers
        </button>
        <button
          className={`acct-subtab${subTab === 'orders' ? ' acct-subtab--active' : ''}`}
          onClick={() => setSubTab('orders')}
        >
          Orders
        </button>
      </div>
      {subTab === 'customers' && <ReferredCustomersTab user={user} />}
      {subTab === 'orders'    && <ReferralOrdersSubTab user={user} />}
    </div>
  );
};

// ── Wallet tab ────────────────────────────────────────────
const TXN_LABEL = { LOAD: 'Added', SPEND: 'Spent', REWARD: 'Reward', REFERRAL: 'Referral' };
const TXN_CLASS = { LOAD: 'wlt-badge--load', SPEND: 'wlt-badge--spend', REWARD: 'wlt-badge--reward', REFERRAL: 'wlt-badge--referral' };
const IS_CREDIT  = { LOAD: true, SPEND: false, REWARD: true, REFERRAL: true };

// Parse commission reference_id into a descriptor the modal can act on.
// Returns { mode: 'batch', partner_type, user_id, start, end }
//      or { mode: 'single', order_id }
//      or null (not a commission transaction).
const getCommissionPayoutRef = (txn) => {
  if (!txn.reference_id) return null;
  if (txn.txn_type !== 'REFERRAL' && txn.txn_type !== 'REWARD') return null;

  // New batch format: COMM-STORE-42-2026-06-01_2026-07-01 or COMM-PROMOTER-134-...
  const batch = txn.reference_id.match(/^COMM-(STORE|PROMOTER)-(\d+)-(\d{4}-\d{2}-\d{2})_(\d{4}-\d{2}-\d{2})$/);
  if (batch) {
    return {
      mode: 'batch',
      partner_type: batch[1] === 'STORE' ? 'store' : 'promoter',
      user_id: parseInt(batch[2], 10),
      start: batch[3],
      end: batch[4],
    };
  }

  // Legacy per-order format: STORE-SELF-123, PROMO-REF-456, etc.
  const single = txn.reference_id.match(/^(?:STORE|PROMO)-(?:SELF|REF)-(\d+)$/);
  if (single) {
    return { mode: 'single', order_id: parseInt(single[1], 10) };
  }

  return null;
};

const WalletTab = ({ user }) => {
  const authHeader = { Authorization: `Bearer ${user.token}` };
  const [balance, setBalance]         = useState(null);
  const [balLoading, setBalLoading]   = useState(true);
  const [txns, setTxns]               = useState([]);
  const [txnLoading, setTxnLoading]   = useState(false);
  const [txnPage, setTxnPage]         = useState(1);
  const [txnPages, setTxnPages]       = useState(0);
  const [phase, setPhase]             = useState('idle'); // idle | entering | processing
  const [loadAmount, setLoadAmount]   = useState('');
  const [error, setError]             = useState('');
  const [commModal, setCommModal] = useState(null); // { txn, payoutRef }

  const fetchBalance = async () => {
    setBalLoading(true);
    try {
      const res = await axios.get(`${API_URL}/wallet/balance`, { headers: authHeader });
      setBalance(res.data);
    } catch {
      setError('Could not load wallet.');
    } finally {
      setBalLoading(false);
    }
  };

  const fetchTxns = async (page = 1) => {
    setTxnLoading(true);
    try {
      const res = await axios.get(`${API_URL}/wallet/transactions`,
        { params: { page }, headers: authHeader });
      setTxns(res.data.transactions);
      setTxnPages(res.data.pages);
      setTxnPage(page);
    } catch { /* silent */ } finally {
      setTxnLoading(false);
    }
  };

  useEffect(() => {
    fetchBalance();
    fetchTxns(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddMoney = async () => {
    const amt = parseFloat(loadAmount);
    if (!amt || amt < 1) { setError('Enter a valid amount (min ₹1).'); return; }
    setError('');
    setPhase('processing');
    try {
      const loaded = await loadRazorpayScript();
      if (!loaded) {
        setError('Failed to load payment SDK. Please check your connection and try again.');
        setPhase('idle');
        return;
      }

      const res = await axios.post(`${API_URL}/wallet/load/create-order`,
        { amount: amt }, { headers: authHeader });
      const { orderId, amount: orderAmt, currency, keyId } = res.data;

      const rzp = new window.Razorpay({
        key: keyId,
        amount: orderAmt,
        currency,
        order_id: orderId,
        name: 'Local Heros',
        description: 'Wallet Top-up',
        handler: async (payment) => {
          try {
            const vRes = await axios.post(`${API_URL}/wallet/load/verify`, {
              razorpay_order_id: payment.razorpay_order_id,
              razorpay_payment_id: payment.razorpay_payment_id,
              razorpay_signature: payment.razorpay_signature,
              amount: orderAmt,
            }, { headers: authHeader });
            setBalance((b) => ({ ...b, cash_balance: vRes.data.cash_balance }));
            setLoadAmount('');
            setPhase('idle');
            fetchTxns(1);
          } catch {
            setError('Payment received. Your wallet balance will be updated shortly.');
            setPhase('idle');
          }
        },
        modal: { ondismiss: () => setPhase('idle') },
        prefill: { name: user.name, email: user.email, contact: user.phone },
        theme: { color: '#D84315' },
      });
      rzp.open();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to initiate payment.');
      setPhase('idle');
    }
  };

  return (
    <div className="wlt-tab">
      {/* Balance cards */}
      {balLoading ? (
        <p className="acct-loading">Loading wallet…</p>
      ) : balance ? (
        <div className="wlt-balance-row">
          <div className="wlt-balance-card">
            <span className="wlt-balance-label">Cash Balance</span>
            <span className="wlt-balance-value">₹{parseFloat(balance.cash_balance).toFixed(2)}</span>
          </div>
          <div className="wlt-balance-card wlt-balance-card--points">
            <span className="wlt-balance-label">Reward Points</span>
            <span className="wlt-balance-value">{parseFloat(balance.points_balance).toFixed(0)} pts</span>
          </div>
        </div>
      ) : null}

      {/* Add money */}
      <div className="wlt-load-section">
        {phase === 'idle' && (
          <button className="wlt-add-btn" onClick={() => setPhase('entering')}>
            + Add Money
          </button>
        )}
        {phase === 'entering' && (
          <div className="wlt-load-form">
            <input
              className="wlt-amount-input"
              type="number"
              min="1"
              max="100000"
              placeholder="Amount in ₹"
              value={loadAmount}
              onChange={(e) => setLoadAmount(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddMoney()}
              autoFocus
            />
            <button className="wlt-pay-btn" onClick={handleAddMoney}>Pay</button>
            <button className="wlt-cancel-btn"
              onClick={() => { setPhase('idle'); setLoadAmount(''); setError(''); }}>
              Cancel
            </button>
          </div>
        )}
        {phase === 'processing' && <p className="acct-loading">Opening payment…</p>}
      </div>

      {error && <p className="acct-error" style={{ margin: '0 0 12px' }}>{error}</p>}

      {commModal && (
        <CommissionDetailModal
          txn={commModal.txn}
          payoutRef={commModal.payoutRef}
          user={user}
          onClose={() => setCommModal(null)}
        />
      )}

      {/* Transaction history */}
      <div className="wlt-txn-section">
        <p className="wlt-txn-heading">Recent Transactions</p>
        {txnLoading ? (
          <p className="acct-loading">Loading…</p>
        ) : txns.length === 0 ? (
          <div className="acct-empty"><p>No transactions yet.</p></div>
        ) : (
          <>
            <ul className="wlt-txn-list">
              {txns.map((t) => {
                const payoutRef = getCommissionPayoutRef(t);
                return (
                  <li
                    key={t.id}
                    className={`wlt-txn-row${payoutRef ? ' wlt-txn-row--drillable' : ''}`}
                    onClick={payoutRef ? () => setCommModal({ txn: t, payoutRef }) : undefined}
                  >
                    <div className="wlt-txn-main">
                      <span className={`wlt-badge ${TXN_CLASS[t.txn_type]}`}>
                        {TXN_LABEL[t.txn_type]}
                      </span>
                      <div className="wlt-txn-desc">
                        <span className="wlt-txn-description">{t.description || t.txn_type}</span>
                        <span className="wlt-txn-date">{formatDate(t.created_date)}</span>
                      </div>
                      <div className="wlt-txn-amounts">
                        <span className={`wlt-txn-amount ${IS_CREDIT[t.txn_type] ? 'wlt-credit' : 'wlt-debit'}`}>
                          {IS_CREDIT[t.txn_type] ? '+' : '−'}₹{parseFloat(t.amount).toFixed(2)}
                        </span>
                        <span className="wlt-txn-balance">
                          Bal: ₹{parseFloat(t.balance_after).toFixed(2)}
                        </span>
                        {payoutRef && <span className="wlt-txn-chevron">›</span>}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
            {txnPages > 1 && (
              <div className="acct-pagination">
                <button className="acct-page-btn"
                  onClick={() => fetchTxns(txnPage - 1)} disabled={txnPage === 1}>
                  &#8592;
                </button>
                <span className="acct-page-info">{txnPage} / {txnPages}</span>
                <button className="acct-page-btn"
                  onClick={() => fetchTxns(txnPage + 1)} disabled={txnPage === txnPages}>
                  &#8594;
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ── Commission payout detail modal ────────────────────────
const fmtDate2  = d => d ? new Date(d).toLocaleDateString('en-IN') : '—';
const fmtPrice2 = n => n != null ? `₹${parseFloat(n).toFixed(2)}` : '—';

const CommissionDetailModal = ({ txn, payoutRef, user, onClose }) => {
  const authHeader = { Authorization: `Bearer ${user.token}` };

  // Shared state for the order list (batch mode) or single order lines (single mode)
  const [orders, setOrders]             = useState([]);
  const [total, setTotal]               = useState(0);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState('');

  // Per-order product-line drill-down (used in both modes)
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [orderDetails, setOrderDetails]       = useState({});
  const [orderDetailsLoading, setOrderDetailsLoading] = useState({});

  useEffect(() => {
    if (payoutRef.mode === 'batch') {
      const { partner_type, user_id, start, end } = payoutRef;
      axios.get(`${API_URL}/commission-payout/breakdown`, {
        params: { user_id, partner_type, start, end },
        headers: authHeader,
      })
        .then(res => { setOrders(res.data.orders); setTotal(res.data.total); })
        .catch(() => setError('Could not load commission details.'))
        .finally(() => setLoading(false));
    } else {
      // Legacy single-order: pre-load the product lines immediately
      axios.get(`${API_URL}/sales-orders/${payoutRef.order_id}/commission`, { headers: authHeader })
        .then(res => {
          setOrders([{
            order_id: payoutRef.order_id,
            order_no: txn.description || `Order #${payoutRef.order_id}`,
            customer_name: null,
            delivery_date: null,
            commission: parseFloat(res.data.total_commission),
            commission_category: txn.txn_type === 'REWARD' ? 'self_sale' : 'referral',
            _lines: res.data.lines,
          }]);
          setTotal(parseFloat(res.data.total_commission));
          setExpandedOrderId(payoutRef.order_id); // auto-expand the single order
        })
        .catch(() => setError('Could not load order details.'))
        .finally(() => setLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleOrder = async (order) => {
    const id = order.order_id;
    if (expandedOrderId === id) { setExpandedOrderId(null); return; }
    setExpandedOrderId(id);
    // If lines already embedded (single mode), no fetch needed
    if (order._lines || orderDetails[id]) return;
    setOrderDetailsLoading(prev => ({ ...prev, [id]: true }));
    try {
      const { data } = await axios.get(`${API_URL}/sales-orders/${id}/commission`, { headers: authHeader });
      setOrderDetails(prev => ({ ...prev, [id]: data }));
    } catch {
      setOrderDetails(prev => ({ ...prev, [id]: null }));
    } finally {
      setOrderDetailsLoading(prev => ({ ...prev, [id]: false }));
    }
  };

  const modalTitle = payoutRef.mode === 'batch' ? 'Commission Payout' : 'Commission Detail';
  const modalSub   = payoutRef.mode === 'batch'
    ? `${payoutRef.partner_type === 'store' ? 'Store' : 'Promoter'} · ${
        txn.description
          ? txn.description.replace(/^Commission payout /, '').replace(/ –.*$/, '')
          : `${payoutRef.start} – ${payoutRef.end}`
      }`
    : txn.description || '';

  const renderLines = (order) => {
    const lines = order._lines || (orderDetails[order.order_id] && orderDetails[order.order_id].lines);
    const detLoad = !order._lines && orderDetailsLoading[order.order_id];
    const failed  = !order._lines && orderDetails[order.order_id] === null;
    return (
      <tr className="wlt-modal-lines-row">
        <td colSpan={6}>
          {detLoad && <p className="wlt-comm-msg">Loading…</p>}
          {failed  && <p className="wlt-comm-msg wlt-comm-err">Failed to load.</p>}
          {lines && (
            <table className="wlt-comm-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th className="wlt-comm-num">Qty</th>
                  <th className="wlt-comm-num">Price</th>
                  <th className="wlt-comm-num">Pre-GST</th>
                  <th className="wlt-comm-num">Comm%</th>
                  <th className="wlt-comm-num">Commission</th>
                </tr>
              </thead>
              <tbody>
                {lines.map(line => (
                  <tr key={line.line_id}>
                    <td>{line.product_name}</td>
                    <td className="wlt-comm-num">{line.qty}</td>
                    <td className="wlt-comm-num">{fmtPrice2(line.unit_price)}</td>
                    <td className="wlt-comm-num">{fmtPrice2(line.pre_gst_unit_price)}</td>
                    <td className="wlt-comm-num">{line.commission_percent}%</td>
                    <td className="wlt-comm-num">{fmtPrice2(line.line_commission)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={5} className="wlt-comm-total-label">Order total</td>
                  <td className="wlt-comm-num wlt-comm-total">{fmtPrice2(order.commission)}</td>
                </tr>
              </tfoot>
            </table>
          )}
        </td>
      </tr>
    );
  };

  return (
    <div className="wlt-modal-overlay" onClick={onClose}>
      <div className="wlt-modal" onClick={e => e.stopPropagation()}>
        <div className="wlt-modal-header">
          <div>
            <p className="wlt-modal-title">{modalTitle}</p>
            {modalSub && <p className="wlt-modal-sub">{modalSub}</p>}
          </div>
          <button className="wlt-modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="wlt-modal-body">
          {loading && <p className="wlt-comm-msg">Loading…</p>}
          {!loading && error && <p className="wlt-comm-msg wlt-comm-err">{error}</p>}
          {!loading && !error && orders.length === 0 && (
            <p className="wlt-comm-msg">No orders found.</p>
          )}
          {!loading && !error && orders.length > 0 && (
            <>
              {payoutRef.mode === 'batch' && (
                <p className="wlt-modal-summary">
                  {orders.length} order{orders.length !== 1 ? 's' : ''} · Total:{' '}
                  <strong>{fmtPrice2(total)}</strong>
                </p>
              )}
              <table className="wlt-modal-table">
                <thead>
                  <tr>
                    <th>Order</th>
                    <th>Customer</th>
                    <th>Date</th>
                    <th>Type</th>
                    <th className="wlt-comm-num">Commission</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map(o => {
                    const isOpen = expandedOrderId === o.order_id;
                    return (
                      <React.Fragment key={o.order_id}>
                        <tr
                          className="wlt-modal-order-row"
                          onClick={() => toggleOrder(o)}
                        >
                          <td>{o.order_no}</td>
                          <td className="wlt-modal-customer">{o.customer_name || '—'}</td>
                          <td>{o.delivery_date ? fmtDate2(o.delivery_date) : '—'}</td>
                          <td>
                            <span className={`wlt-modal-cat wlt-modal-cat--${o.commission_category}`}>
                              {o.commission_category === 'self_sale' ? 'Self' : 'Referral'}
                            </span>
                          </td>
                          <td className="wlt-comm-num">{fmtPrice2(o.commission)}</td>
                          <td className="wlt-modal-chevron">{isOpen ? '▲' : '▼'}</td>
                        </tr>
                        {isOpen && renderLines(o)}
                      </React.Fragment>
                    );
                  })}
                </tbody>
                {payoutRef.mode === 'batch' && (
                  <tfoot>
                    <tr>
                      <td colSpan={4} className="wlt-modal-total-label">Total commission credited</td>
                      <td className="wlt-comm-num wlt-modal-total">{fmtPrice2(total)}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ── helpers ───────────────────────────────────────────────
const encodeRef = (payload) =>
  btoa(unescape(encodeURIComponent(JSON.stringify(payload))))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');

const BASE_URL = window.location.origin;

// ── DMP Invite IP tab ─────────────────────────────────────
const DMPReferTab = ({ user }) => {
  const [copied, setCopied] = useState(false);

  const link = `${BASE_URL}/register?ref=${encodeRef({ rid: user.id, rn: user.name })}`;

  const waUrl = `https://wa.me/?text=${encodeURIComponent(
    `Hi! I'd like to invite you to join as an Influencer Partner on Local Heros.\n\nRegister here: ${link}`
  )}`;

  const handleCopy = () => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(link).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }).catch(() => fallbackCopy());
    } else {
      fallbackCopy();
    }
  };

  const fallbackCopy = () => {
    const ta = document.createElement('textarea');
    ta.value = link;
    ta.style.cssText = 'position:fixed;top:0;left:0;opacity:0;';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    try { document.execCommand('copy'); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch (e) {}
    document.body.removeChild(ta);
  };

  return (
    <div className="acct-refer">
      <p className="acct-refer-desc">
        Share this link with anyone you want to invite as an Influencer Partner (IP).
        When they register using this link, they will automatically be set up as an IP under you.
      </p>

      <div className="acct-refer-link-row" style={{ marginBottom: '16px' }}>
        <span className="acct-refer-link">{link}</span>
        <button className="acct-refer-copy" onClick={handleCopy} title="Copy link">
          {copied ? '✓' : '📋'}
        </button>
      </div>

      <div className="acct-refer-actions">
        <button className="acct-refer-btn acct-refer-btn--primary" onClick={handleCopy}>
          {copied ? 'Copied!' : 'Copy Invite Link'}
        </button>
        <a
          href={waUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="acct-refer-btn acct-refer-btn--invite"
        >
          &#128172; Share via WhatsApp
        </a>
      </div>
    </div>
  );
};

// ── Refer a Customer tab ──────────────────────────────────
const cleanPhone = (raw) => {
  let p = (raw || '').replace(/[\s\-.]/g, '');
  if (p.startsWith('+91')) p = p.slice(3);
  else if (/^91\d{10}$/.test(p)) p = p.slice(2);
  return p;
};

const ReferTab = ({ user }) => {
  const [form, setForm]       = useState({ name: '', phone: '', email: '', address: '' });
  const [link, setLink]       = useState('');
  const [waUrl, setWaUrl]     = useState('');
  const [copied, setCopied]   = useState(false);
  const [error, setError]     = useState('');
  const [checking, setChecking] = useState(false);

  const buildLink = async () => {
    const phone = cleanPhone(form.phone);
    if (!form.name.trim()) { setError('Customer name is required.'); return null; }
    if (!phone)             { setError('Phone number is required.'); return null; }
    if (!/^\d{10}$/.test(phone)) { setError('Enter a valid 10-digit phone number.'); return null; }
    const email = form.email.trim().toLowerCase();
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Enter a valid email address.'); return null;
    }

    const phoneRes = await axios.get(`${API_URL}/authRoutes/check-phone`, { params: { phone } });
    if (phoneRes.data.exists) { setError('This phone number is already registered.'); return null; }

    if (email) {
      const emailRes = await axios.get(`${API_URL}/authRoutes/check-email`, { params: { email } });
      if (emailRes.data.exists) { setError('This email address is already registered.'); return null; }
    }

    const pincodes = [...new Set((form.address.match(/\b\d{6}\b/g) || []))];
    if (pincodes.length > 0) {
      let deliverable = false;
      for (const pincode of pincodes) {
        const pinRes = await axios.get(`${API_URL}/authRoutes/check-pincode`, { params: { pincode } });
        if (pinRes.data.deliverable) { deliverable = true; break; }
      }
      if (!deliverable) { setError('Currently we are not delivering at this pincode.'); return null; }
    }

    const payload = {
      n:   form.name.trim(),
      p:   phone,
      rid: user.id,
      rn:  user.name || user.username || '',
    };
    if (email)               payload.e = email;
    if (form.address.trim()) payload.a = form.address.trim();
    return `${BASE_URL}/register?ref=${encodeRef(payload)}`;
  };

  const handleGenerate = async () => {
    setError(''); setLink('');
    setChecking(true);
    try {
      const generated = await buildLink();
      if (generated) setLink(generated);
    } catch {
      setError('Could not validate details. Please try again.');
    } finally {
      setChecking(false);
    }
  };

  const handleInvite = async () => {
    setError(''); setLink(''); setWaUrl('');
    setChecking(true);
    try {
      const generated = await buildLink();
      if (!generated) return;
      setLink(generated);
      const phone = cleanPhone(form.phone);
      const senderName = user.name || user.username || '';
      const message =
        `Hi ${form.name.trim()}! 👋\n\n` +
        `I'd like to invite you to The New Luxury – clean, curated food delivered to your doorstep.\n\n` +
        `Use this personal invitation link to register:\n${generated}\n\n` +
        `Regards,\n${senderName}\nThe New Luxury`;
      setWaUrl(`https://wa.me/91${phone}?text=${encodeURIComponent(message)}`);
    } catch {
      setError('Could not validate details. Please try again.');
    } finally {
      setChecking(false);
    }
  };

  const handleCopy = () => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(link).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }).catch(() => fallbackCopy());
    } else {
      fallbackCopy();
    }
  };

  const fallbackCopy = () => {
    const ta = document.createElement('textarea');
    ta.value = link;
    ta.style.cssText = 'position:fixed;top:0;left:0;opacity:0;';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    try {
      document.execCommand('copy');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      setError('Could not copy — please copy the link manually.');
    }
    document.body.removeChild(ta);
  };

  return (
    <div className="acct-refer">
      <p className="acct-refer-desc">
        Fill in the customer details to generate a shareable registration link.
      </p>

      {error && <p className="acct-refer-error">{error}</p>}

      <div className="acct-refer-form">
        <div className="acct-refer-field">
          <label>Customer Name *</label>
          <input
            type="text"
            value={form.name}
            onChange={e => { setForm({ ...form, name: e.target.value }); setWaUrl(''); }}
            placeholder="Enter customer name"
          />
        </div>
        <div className="acct-refer-field">
          <label>Phone Number *</label>
          <input
            type="tel"
            value={form.phone}
            onChange={e => { setForm({ ...form, phone: e.target.value }); setWaUrl(''); }}
            placeholder="Enter phone number"
          />
        </div>
        <div className="acct-refer-field">
          <label>Email Address <span style={{ fontWeight: 400, opacity: 0.7 }}>(optional)</span></label>
          <input
            type="email"
            value={form.email}
            onChange={e => { setForm({ ...form, email: e.target.value }); setWaUrl(''); }}
            placeholder="Enter email address"
          />
        </div>
        <div className="acct-refer-field">
          <label>Address <span style={{ fontWeight: 400, opacity: 0.7 }}>(optional)</span></label>
          <textarea
            value={form.address}
            onChange={e => { setForm({ ...form, address: e.target.value }); setWaUrl(''); }}
            placeholder="Enter delivery address"
            rows={4}
            style={{ width: '100%', resize: 'vertical', fontSize: '0.95rem', padding: '8px 10px', boxSizing: 'border-box' }}
          />
        </div>
      </div>

      <div className="acct-refer-actions">
        <button className="acct-refer-btn acct-refer-btn--primary" onClick={handleGenerate} disabled={checking}>
          {checking ? 'Checking…' : 'Generate Referral Link'}
        </button>
        {waUrl ? (
          <a
            href={waUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="acct-refer-btn acct-refer-btn--invite"
          >
            &#128172; Open WhatsApp
          </a>
        ) : (
          <button className="acct-refer-btn acct-refer-btn--invite" onClick={handleInvite} disabled={checking}>
            {checking ? 'Preparing…' : <span>&#128172; Invite via WhatsApp</span>}
          </button>
        )}
      </div>

      {link && (
        <div className="acct-refer-link-row">
          <span className="acct-refer-link">{link}</span>
          <button className="acct-refer-copy" onClick={handleCopy} title="Copy link">
            {copied ? '✓' : '📋'}
          </button>
        </div>
      )}
    </div>
  );
};

// ── Subscriptions tab ─────────────────────────────────────
const DAYS_LABEL = { AD: 'Every Day', WD: 'Weekdays', WE: 'Weekends' };
const SUB_STATUS_CLASS = {
  active:    'sub-status--active',
  paused:    'sub-status--paused',
  cancelled: 'sub-status--cancelled',
};

const SubscriptionsTab = ({ user }) => {
  const authHeader = { Authorization: `Bearer ${user.token}` };
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [actionError, setActionError] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/subscriptions`, { headers: authHeader })
      .then(res => { setSubscriptions(res.data); setLoading(false); })
      .catch(() => { setError('Could not load subscriptions.'); setLoading(false); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePause = async (id) => {
    setActionError(null);
    setActionLoading(id);
    try {
      await axios.patch(`${API_URL}/subscriptions/${id}/pause`, {}, { headers: authHeader });
      setSubscriptions(prev => prev.map(s => s.id === id ? { ...s, status: 'paused' } : s));
    } catch (err) {
      setActionError(err.response?.data?.error || 'Could not pause subscription.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleResume = async (id) => {
    setActionError(null);
    setActionLoading(id);
    try {
      await axios.patch(`${API_URL}/subscriptions/${id}/resume`, {}, { headers: authHeader });
      setSubscriptions(prev => prev.map(s => s.id === id ? { ...s, status: 'active' } : s));
    } catch (err) {
      setActionError(err.response?.data?.error || 'Could not resume subscription.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancel = async (id) => {
    setActionError(null);
    if (!window.confirm('Cancel this subscription? Future scheduled orders will be skipped.')) return;
    setActionLoading(id);
    try {
      await axios.delete(`${API_URL}/subscriptions/${id}`, { headers: authHeader });
      setSubscriptions(prev => prev.map(s => s.id === id ? { ...s, status: 'cancelled' } : s));
    } catch (err) {
      setActionError(err.response?.data?.error || 'Could not cancel subscription.');
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) return <p className="acct-loading">Loading subscriptions…</p>;
  if (error)   return <p className="acct-error">{error}</p>;

  if (subscriptions.length === 0) return (
    <div className="acct-empty">
      <p>No active subscriptions.</p>
      <p style={{ fontSize: '13px', marginTop: '8px' }}>
        Subscribe to a product from the Home page to set up recurring deliveries.
      </p>
    </div>
  );

  return (
    <div className="acct-subs">
      {actionError && <p className="acct-error" style={{ marginBottom: '12px' }}>{actionError}</p>}

      {subscriptions.map(sub => (
        <div key={sub.id} className="acct-order-card">
          <button
            className="acct-order-header"
            onClick={() => setExpanded(expanded === sub.id ? null : sub.id)}
          >
            <div className="acct-order-meta">
              <span className="acct-order-no">{sub.product_name}</span>
              <span className="acct-order-date">
                {DAYS_LABEL[sub.days_of_week] || sub.days_of_week}
                {' · '}Qty {sub.quantity}
                {' · '}&#8377;{parseFloat(sub.unit_price).toFixed(2)}/delivery
              </span>
            </div>
            <div className="acct-order-right">
              <span className={`acct-order-status ${SUB_STATUS_CLASS[sub.status] || 'status-default'}`}>
                {sub.status}
              </span>
              <span className="acct-order-chevron">{expanded === sub.id ? '▲' : '▼'}</span>
            </div>
          </button>

          {expanded === sub.id && (
            <div className="acct-order-body">
              <div className="acct-sub-details">
                {sub.delivery_window && (
                  <div className="acct-sub-detail-row">
                    <span className="acct-sub-detail-label">Window</span>
                    <span>{sub.delivery_window}</span>
                  </div>
                )}
                <div className="acct-sub-detail-row">
                  <span className="acct-sub-detail-label">From</span>
                  <span>{formatDate(sub.start_date)}</span>
                </div>
                {sub.end_date && (
                  <div className="acct-sub-detail-row">
                    <span className="acct-sub-detail-label">Until</span>
                    <span>{formatDate(sub.end_date)}</span>
                  </div>
                )}
                {!sub.end_date && (
                  <div className="acct-sub-detail-row">
                    <span className="acct-sub-detail-label">Duration</span>
                    <span>Ongoing</span>
                  </div>
                )}
              </div>

              {sub.status !== 'cancelled' && (
                <div className="acct-sub-actions">
                  {sub.status === 'active' && (
                    <button
                      className="acct-sub-btn acct-sub-btn--pause"
                      onClick={() => handlePause(sub.id)}
                      disabled={actionLoading === sub.id}
                    >
                      {actionLoading === sub.id ? '…' : 'Pause'}
                    </button>
                  )}
                  {sub.status === 'paused' && (
                    <button
                      className="acct-sub-btn acct-sub-btn--resume"
                      onClick={() => handleResume(sub.id)}
                      disabled={actionLoading === sub.id}
                    >
                      {actionLoading === sub.id ? '…' : 'Resume'}
                    </button>
                  )}
                  <button
                    className="acct-sub-btn acct-sub-btn--cancel"
                    onClick={() => handleCancel(sub.id)}
                    disabled={actionLoading === sub.id}
                  >
                    {actionLoading === sub.id ? '…' : 'Cancel'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// ── Weekly Payments tab ───────────────────────────────────
const WeeklyPaymentsTab = ({ user }) => {
  const authHeader = { Authorization: `Bearer ${user.token}` };
  const [weeks, setWeeks]       = useState([]);
  const [history, setHistory]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [payError, setPayError] = useState('');
  const [payingKey, setPayingKey] = useState(null);

  const [expandedWeek, setExpandedWeek]               = useState(null);
  const [expandedHistoryWeek, setExpandedHistoryWeek] = useState(null);
  const [expandedOrder, setExpandedOrder]             = useState(null);
  const [orderDetails, setOrderDetails]               = useState({});
  const [orderDetailsLoading, setOrderDetailsLoading] = useState({});

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [summaryRes, historyRes] = await Promise.all([
        axios.get(`${API_URL}/pay/weekly-summary`, { headers: authHeader }),
        axios.get(`${API_URL}/pay/weekly-payment-history`, { headers: authHeader }),
      ]);
      setWeeks(summaryRes.data);
      setHistory(historyRes.data);
    } catch {
      setError('Could not load payments.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const fmtWeekLabel = (start, end) => `${formatDate(start)} – ${formatDate(end)}`;

  const toggleWeek = (weekStart) => {
    setExpandedWeek(prev => prev === weekStart ? null : weekStart);
    setExpandedOrder(null);
  };

  const toggleOrder = async (orderId) => {
    if (expandedOrder === orderId) { setExpandedOrder(null); return; }
    setExpandedOrder(orderId);
    if (!orderDetails[orderId]) {
      setOrderDetailsLoading(prev => ({ ...prev, [orderId]: true }));
      try {
        const { data } = await axios.get(
          `${API_URL}/sales-orders/${orderId}/commission`,
          { headers: authHeader }
        );
        setOrderDetails(prev => ({ ...prev, [orderId]: data }));
      } catch {
        setOrderDetails(prev => ({ ...prev, [orderId]: null }));
      } finally {
        setOrderDetailsLoading(prev => ({ ...prev, [orderId]: false }));
      }
    }
  };

  const handlePayNow = async (week) => {
    setPayError('');
    setPayingKey(week.week_start);

    const loaded = await loadRazorpayScript();
    if (!loaded) {
      setPayError('Failed to load payment SDK. Please check your connection.');
      setPayingKey(null);
      return;
    }

    try {
      const { data } = await axios.post(
        `${API_URL}/pay/weekly-payment/create-order`,
        { week_start: week.week_start, order_ids: (week.orders || []).map(o => o.id) },
        { headers: authHeader }
      );

      const cancelPending = () =>
        axios.post(`${API_URL}/pay/weekly-payment/cancel`,
          { razorpay_order_id: data.razorpay_order_id },
          { headers: authHeader }
        ).catch(() => {});

      const rzp = new window.Razorpay({
        key:      data.key,
        amount:   data.amount,
        currency: data.currency,
        order_id: data.razorpay_order_id,
        name:     'Local Heros',
        description: `Weekly payment (${fmtWeekLabel(week.week_start, week.week_end)})`,
        prefill: { name: user.name, email: user.email, contact: user.phone },
        theme:   { color: '#C9A227' },
        handler: async (response) => {
          try {
            await axios.post(`${API_URL}/pay/weekly-payment/verify`, {
              razorpay_order_id:   response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature:  response.razorpay_signature,
            }, { headers: authHeader });
            setPayingKey(null);
            fetchData();
          } catch {
            setPayError('Payment received but update failed. Please contact support.');
            setPayingKey(null);
          }
        },
        modal: {
          ondismiss: () => {
            cancelPending();
            setPayingKey(null);
          },
        },
      });
      rzp.on('payment.failed', (response) => {
        cancelPending();
        setPayError(`Payment failed: ${response.error.description}`);
        setPayingKey(null);
      });
      rzp.open();
    } catch (err) {
      setPayError(err.response?.data?.error || 'Failed to initiate payment. Please try again.');
      setPayingKey(null);
    }
  };

  if (loading) return <p className="acct-loading">Loading payments…</p>;
  if (error)   return <p className="acct-error">{error}</p>;
  if (weeks.length === 0 && history.length === 0) return (
    <div className="acct-empty"><p>No payments yet.</p></div>
  );

  return (
    <div className="wkpay-list">
      {payError && <p className="acct-error" style={{ marginBottom: 12 }}>{payError}</p>}

      {/* Pending weeks */}
      {weeks.map(week => {
        const isOpen = expandedWeek === week.week_start;
        return (
          <div key={week.week_start} className="wkpay-card">
            {/* Card header row — click info side to expand */}
            <div className="wkpay-card-header">
              <div className="wkpay-info" onClick={() => toggleWeek(week.week_start)} style={{ cursor: 'pointer' }}>
                <span className="wkpay-label">
                  {fmtWeekLabel(week.week_start, week.week_end)}
                  <span className="wkpay-chevron">{isOpen ? ' ▲' : ' ▼'}</span>
                </span>
                <span className="wkpay-meta">
                  {week.order_count} order{week.order_count !== 1 ? 's' : ''}
                  {!week.is_payable && <span className="wkpay-pending-note"> · payable on Sunday</span>}
                </span>
                {week.total_commission > 0 && (
                  <span className="wkpay-commission-row">
                    <span className="wkpay-gross">{formatPrice(week.gross_total)}</span>
                    <span className="wkpay-commission-deduct">− {formatPrice(week.total_commission)} commission</span>
                  </span>
                )}
              </div>
              <div className="wkpay-right">
                <span className="wkpay-total">{formatPrice(week.net_total)}</span>
                <button
                  className="wkpay-pay-btn"
                  onClick={() => handlePayNow(week)}
                  disabled={!week.is_payable || payingKey === week.week_start}
                >
                  {payingKey === week.week_start ? 'Opening…' : 'Pay Now'}
                </button>
              </div>
            </div>

            {/* Expanded order list */}
            {isOpen && (
              <div className="wkpay-orders">
                {(week.orders || []).map(order => {
                  const orderOpen = expandedOrder === order.id;
                  const detail    = orderDetails[order.id];
                  const detLoading = orderDetailsLoading[order.id];
                  return (
                    <div key={order.id} className="wkpay-order-row">
                      <div className="wkpay-order-header" onClick={() => toggleOrder(order.id)}>
                        <div className="wkpay-order-left">
                          <span className="wkpay-order-no">{order.order_no}</span>
                          <span className="wkpay-order-date">{formatDate(order.delivery_date || order.order_date)}</span>
                        </div>
                        <div className="wkpay-order-right">
                          <span className="wkpay-order-amount">{formatPrice(order.order_amount)}</span>
                          {order.commission > 0 && (
                            <span className="wkpay-order-comm">−{formatPrice(order.commission)}</span>
                          )}
                          <span className="wkpay-chevron-sm">{orderOpen ? '▲' : '▼'}</span>
                        </div>
                      </div>

                      {/* Product line details */}
                      {orderOpen && (
                        <div className="wkpay-lines">
                          {detLoading && <p className="wkpay-lines-msg">Loading…</p>}
                          {!detLoading && !detail && <p className="wkpay-lines-msg wkpay-lines-err">Failed to load details.</p>}
                          {!detLoading && detail && (
                            <table className="wkpay-lines-table">
                              <thead>
                                <tr>
                                  <th>Product</th>
                                  <th className="wkpay-num">Qty</th>
                                  <th className="wkpay-num">Price</th>
                                  <th className="wkpay-num">Pre-GST</th>
                                  <th className="wkpay-num">Comm%</th>
                                  <th className="wkpay-num">Commission</th>
                                </tr>
                              </thead>
                              <tbody>
                                {detail.lines.map(line => (
                                  <tr key={line.line_id}>
                                    <td>{line.product_name}</td>
                                    <td className="wkpay-num">{line.qty}</td>
                                    <td className="wkpay-num">{formatPrice(line.unit_price)}</td>
                                    <td className="wkpay-num">{formatPrice(line.pre_gst_unit_price)}</td>
                                    <td className="wkpay-num">{line.commission_percent}%</td>
                                    <td className="wkpay-num">{formatPrice(line.line_commission)}</td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot>
                                <tr>
                                  <td colSpan={5} className="wkpay-lines-total-label">Order total commission</td>
                                  <td className="wkpay-num wkpay-lines-total">{formatPrice(detail.total_commission)}</td>
                                </tr>
                              </tfoot>
                            </table>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {/* Payment history */}
      {history.length > 0 && (
        <>
          <p className="wkpay-history-heading">Payment History</p>
          {history.map(entry => {
            const isOpen = expandedHistoryWeek === entry.week_start;
            return (
              <div key={entry.week_start} className="wkpay-card wkpay-card--paid">
                <div className="wkpay-card-header">
                  <div className="wkpay-info" onClick={() => setExpandedHistoryWeek(prev => prev === entry.week_start ? null : entry.week_start)} style={{ cursor: 'pointer' }}>
                    <span className="wkpay-label">
                      {fmtWeekLabel(entry.week_start, entry.week_end)}
                      <span className="wkpay-chevron">{isOpen ? ' ▲' : ' ▼'}</span>
                    </span>
                    <span className="wkpay-meta">
                      {entry.order_count} order{entry.order_count !== 1 ? 's' : ''}
                      {' · '}Paid on {formatDate(entry.completed_date)}
                    </span>
                    {entry.total_commission > 0 && (
                      <span className="wkpay-commission-row">
                        <span className="wkpay-gross">{formatPrice(entry.gross_total)}</span>
                        <span className="wkpay-commission-deduct">− {formatPrice(entry.total_commission)} commission</span>
                      </span>
                    )}
                  </div>
                  <div className="wkpay-right">
                    <span className="wkpay-total">{formatPrice(entry.net_total)}</span>
                    <span className="wkpay-paid-badge">Paid</span>
                  </div>
                </div>

                {isOpen && (
                  <div className="wkpay-orders">
                    {(entry.orders || []).map(order => {
                      const orderOpen  = expandedOrder === order.id;
                      const detail     = orderDetails[order.id];
                      const detLoading = orderDetailsLoading[order.id];
                      return (
                        <div key={order.id} className="wkpay-order-row">
                          <div className="wkpay-order-header" onClick={() => toggleOrder(order.id)}>
                            <div className="wkpay-order-left">
                              <span className="wkpay-order-no">{order.order_no}</span>
                              <span className="wkpay-order-date">{formatDate(order.delivery_date || order.order_date)}</span>
                            </div>
                            <div className="wkpay-order-right">
                              <span className="wkpay-order-amount">{formatPrice(order.order_amount)}</span>
                              {order.commission > 0 && (
                                <span className="wkpay-order-comm">−{formatPrice(order.commission)}</span>
                              )}
                              <span className="wkpay-chevron-sm">{orderOpen ? '▲' : '▼'}</span>
                            </div>
                          </div>

                          {orderOpen && (
                            <div className="wkpay-lines">
                              {detLoading && <p className="wkpay-lines-msg">Loading…</p>}
                              {!detLoading && !detail && <p className="wkpay-lines-msg wkpay-lines-err">Failed to load details.</p>}
                              {!detLoading && detail && (
                                <table className="wkpay-lines-table">
                                  <thead>
                                    <tr>
                                      <th>Product</th>
                                      <th className="wkpay-num">Qty</th>
                                      <th className="wkpay-num">Price</th>
                                      <th className="wkpay-num">Pre-GST</th>
                                      <th className="wkpay-num">Comm%</th>
                                      <th className="wkpay-num">Commission</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {detail.lines.map(line => (
                                      <tr key={line.line_id}>
                                        <td>{line.product_name}</td>
                                        <td className="wkpay-num">{line.qty}</td>
                                        <td className="wkpay-num">{formatPrice(line.unit_price)}</td>
                                        <td className="wkpay-num">{formatPrice(line.pre_gst_unit_price)}</td>
                                        <td className="wkpay-num">{line.commission_percent}%</td>
                                        <td className="wkpay-num">{formatPrice(line.line_commission)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                  <tfoot>
                                    <tr>
                                      <td colSpan={5} className="wkpay-lines-total-label">Order total commission</td>
                                      <td className="wkpay-num wkpay-lines-total">{formatPrice(detail.total_commission)}</td>
                                    </tr>
                                  </tfoot>
                                </table>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
};

// ── Account page ──────────────────────────────────────────
const Account = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isPartner = user?.user_type === 'P' || user?.user_type === 'S';
  const isCS           = user?.user_type === 'CS';
  const isStorePartner = user?.user_type === 'S';
  const isStoreOrCS    = isStorePartner || isCS;
  const isDMP = user?.user_type === 'DMP';
  const isIP  = user?.user_type === 'IP';
  const [tab, setTab] = useState(() => {
    const p = searchParams.get('tab');
    if (p) return p;
    return isStoreOrCS ? 'stocks' : 'orders';
  });
  const [checkoutMode, setCheckoutModeState] = useState(
    () => localStorage.getItem('checkoutMode') || 'live'
  );

  const setCheckoutMode = (mode) => {
    localStorage.setItem('checkoutMode', mode);
    setCheckoutModeState(mode);
  };

  return (
    <div className="acct-container">
      <div className="acct-header">
        <div className="acct-avatar">
          {(user?.name || user?.username || 'U')[0].toUpperCase()}
        </div>
        <div>
          <h2 className="acct-name">{user?.name || user?.username}</h2>
          <p className="acct-email">{user?.email || user?.username}</p>
        </div>
        {isStorePartner && (
          <div className="acct-mode-toggle">
            <button
              className={`acct-mode-btn${checkoutMode === 'live' ? ' acct-mode-btn--active' : ''}`}
              onClick={() => setCheckoutMode('live')}
            >
              Live
            </button>
            <button
              className={`acct-mode-btn${checkoutMode === 'prebook' ? ' acct-mode-btn--active' : ''}`}
              onClick={() => setCheckoutMode('prebook')}
            >
              Prebook
            </button>
          </div>
        )}
      </div>

      <div className="acct-tabs">
        {isStoreOrCS && (
          <button
            className={`acct-tab ${tab === 'stocks' ? 'acct-tab--active' : ''}`}
            onClick={() => setTab('stocks')}
          >
            &#128230; Stocks
          </button>
        )}
        <button
          className={`acct-tab ${tab === 'orders' ? 'acct-tab--active' : ''}`}
          onClick={() => setTab('orders')}
        >
          &#128230; Orders
        </button>
        {isPartner && (
          <button
            className={`acct-tab ${tab === 'refer' ? 'acct-tab--active' : ''}`}
            onClick={() => setTab('refer')}
          >
            &#128101; New Customer
          </button>
        )}
        {isPartner && (
          <button
            className={`acct-tab ${tab === 'referrals' ? 'acct-tab--active' : ''}`}
            onClick={() => setTab('referrals')}
          >
            &#128101; Customers
          </button>
        )}
        {isDMP && (
          <button
            className={`acct-tab ${tab === 'invite-ip' ? 'acct-tab--active' : ''}`}
            onClick={() => setTab('invite-ip')}
          >
            &#128279; Invite IP
          </button>
        )}
        {isIP && (
          <button
            className={`acct-tab ${tab === 'referrals' ? 'acct-tab--active' : ''}`}
            onClick={() => setTab('referrals')}
          >
            &#128101; Customers
          </button>
        )}
        {user?.bypass_payment === 'Y' && (
          <button
            className={`acct-tab ${tab === 'payments' ? 'acct-tab--active' : ''}`}
            onClick={() => setTab('payments')}
          >
            &#128176; Payments
          </button>
        )}
        {!isStoreOrCS && (
          <button
            className={`acct-tab ${tab === 'wallet' ? 'acct-tab--active' : ''}`}
            onClick={() => setTab('wallet')}
          >
            &#128179; Wallet
          </button>
        )}
        <button
          className={`acct-tab ${tab === 'details' ? 'acct-tab--active' : ''}`}
          onClick={() => setTab('details')}
        >
          &#128100; Personal Details
        </button>
      </div>

      <div className="acct-panel">
        {tab === 'stocks'    && isStoreOrCS    && <StocksTab user={user} />}
        {tab === 'orders'    && <OrdersTab user={user} />}
        {tab === 'payments' && user?.bypass_payment === 'Y' && <WeeklyPaymentsTab user={user} />}
        {tab === 'refer'     && isPartner && <ReferTab user={user} />}
        {tab === 'referrals' && (isPartner || isIP) && <ReferralsTab user={user} />}
        {tab === 'invite-ip' && isDMP    && <DMPReferTab user={user} />}
        {tab === 'wallet'          && <WalletTab user={user} />}
        {tab === 'details'         && <PersonalDetailsTab user={user} />}
      </div>
    </div>
  );
};

export default Account;
